import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  Archive,
  ArrowDown,
  ArrowUp,
  BriefcaseBusiness,
  Camera,
  CheckCircle2,
  Clock3,
  ClipboardList,
  Eye,
  Filter,
  Flag,
  ImagePlus,
  Inbox,
  Loader2,
  MapPin,
  MessageCircle,
  PauseCircle,
  RefreshCw,
  Save,
  Search,
  Send,
  ShieldAlert,
  Trash2,
  Upload,
  UserRound,
  X,
} from 'lucide-react';
import toast from 'react-hot-toast';
import useAuthStore from '../store/useAuthStore';
import { normalizeRole, ROLE_KEYS } from '../utils/rolePermissions';
import {
  acceptDoctorCircleConsultOffer,
  archiveDoctorCircleCasePhoto,
  archiveMyDoctorCircleProfile,
  cancelDoctorCircleConsultOffer,
  createDoctorCircleConsultOffer,
  createDoctorCircleReport,
  declineDoctorCircleConsultOffer,
  getDoctorCircleChatMessages,
  getDoctorCircleOfferById,
  getDoctorCircleUnlockPaymentStatus,
  getMyDoctorCircleProfile,
  getMyDoctorCircleIncomingOffers,
  getMyDoctorCircleSentOffers,
  getPublishedDoctorCircleProfile,
  initiateDoctorCircleUnlockPayment,
  markDoctorCircleOfferViewed,
  markDoctorCircleChatThreadRead,
  openDoctorCircleChatForOffer,
  pauseMyDoctorCircleProfile,
  publishMyDoctorCircleProfile,
  reorderDoctorCircleCasePhotos,
  saveMyDoctorCircleProfile,
  searchPublishedDoctorCircleProfiles,
  sendDoctorCircleChatMessage,
  updateDoctorCircleAvailability,
  uploadDoctorCircleCasePhoto,
  uploadMyDoctorCircleProfilePhoto,
  removeMyDoctorCircleProfilePhoto,
} from '../services/doctorsCircleApi';

const RUPEE_SYMBOL = '\u20B9';
const PROFILE_PHOTO_MAX_BYTES = 5 * 1024 * 1024;
const PROFILE_PHOTO_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);
const PROFILE_PHOTO_EXTENSIONS = new Set(['jpg', 'jpeg', 'png', 'webp']);
const PROFILE_MANAGER_ROLES = [
  ROLE_KEYS.OWNER_ADMIN,
  ROLE_KEYS.DOCTOR,
  ROLE_KEYS.CONSULTANT_DOCTOR,
];
const SEARCH_ACCESS_ROLES = [
  ROLE_KEYS.OWNER_ADMIN,
  ROLE_KEYS.DOCTOR,
  ROLE_KEYS.STAFF,
  ROLE_KEYS.ACCOUNTANT,
  ROLE_KEYS.CONSULTANT_DOCTOR,
  ROLE_KEYS.PLATFORM_ADMIN,
];
const CONSULT_OFFER_CREATOR_ROLES = [
  ROLE_KEYS.OWNER_ADMIN,
  ROLE_KEYS.DOCTOR,
];
const CONSULT_OFFER_VIEWER_ROLES = [
  ROLE_KEYS.OWNER_ADMIN,
  ROLE_KEYS.DOCTOR,
  ROLE_KEYS.CONSULTANT_DOCTOR,
];
const EMPTY_PROFILE_FORM = {
  displayName: '',
  degree: '',
  education: '',
  specialization: '',
  yearsOfExperience: '',
  areaOfAvailability: '',
  timeOfAvailability: '',
  feesPerService: '',
  shortMessage: '',
  profilePhotoUrl: '',
  isOpenToWork: true,
};
const EMPTY_CASE_PHOTO_FORM = {
  photoType: 'portfolio',
  containsNoTextOrNumbersAttested: false,
};
const EMPTY_AVAILABILITY_ROW = {
  areaName: '',
  availableDaysText: '',
  startTime: '09:00',
  endTime: '17:00',
  notes: '',
  isActive: true,
};
const EMPTY_SEARCH_FILTERS = {
  search: '',
  education: '',
  specialization: '',
  area: '',
  availability: '',
  openToWork: true,
  minimumYearsExperience: '',
  sortBy: 'relevance',
};
const SPECIALIZATION_SEARCH_OPTIONS = [
  'Orthodontist',
  'Endodontist',
  'Periodontist',
  'Prosthodontist',
  'Oral & Maxillofacial Surgeon',
  'Pedodontist',
  'Oral Pathologist',
  'General Dentist',
];
const AREA_SEARCH_OPTIONS = [
  'Delhi',
  'NCR',
  'Gurgaon',
  'Noida',
  'Mumbai',
  'Bangalore',
  'Jaipur',
  'Pune',
  'Hyderabad',
  'Chennai',
  'Kolkata',
];
const AVAILABILITY_SEARCH_OPTIONS = [
  'Mornings',
  'Afternoons',
  'Evenings',
  'Weekends only',
  'Sundays only',
  'After 2 PM',
  '9 AM - 5 PM',
];
const EMPTY_OFFER_FORM = {
  appointmentDateTime: '',
  offeredAmount: '',
  safeServiceSummary: '',
  shortMessage: '',
  consultationCategory: '',
  anonymousClinicArea: '',
};
const DECLINE_REASON_OPTIONS = [
  'Schedule busy',
  'Not in town',
  'Not available at requested time',
  'Fees too low',
  'Other',
];
const EMPTY_DECLINE_FORM = {
  reasonType: DECLINE_REASON_OPTIONS[0],
  otherReason: '',
};
const REPORT_REASON_OPTIONS = [
  { value: 'fake_profile', label: 'Fake profile' },
  { value: 'inappropriate_content', label: 'Inappropriate content' },
  { value: 'patient_privacy_violation', label: 'Patient privacy violation' },
  { value: 'contact_detail_leak', label: 'Contact detail leak' },
  { value: 'spam', label: 'Spam' },
  { value: 'harassment', label: 'Harassment' },
  { value: 'payment_dispute', label: 'Payment dispute' },
  { value: 'other', label: 'Other' },
];
const EMPTY_REPORT_FORM = {
  reasonType: 'inappropriate_content',
  reasonText: '',
};
const PRIMARY_TAB_ITEMS = [
  { id: 'findConsultants', label: 'Find Consultant', icon: Search },
  { id: 'profile', label: 'My Consultant Profile', icon: UserRound },
];
const PROFILE_WORKSPACE_TAB_IDS = ['profile', 'casePhotos', 'sentOffers', 'incomingOffers', 'availability'];
const PROFILE_WORKSPACE_TAB_ITEMS = [
  { id: 'profile', label: 'Profile', icon: UserRound },
  { id: 'casePhotos', label: 'Case Photos', icon: Camera },
  { id: 'sentOffers', label: 'My Sent Offers', icon: ClipboardList },
  { id: 'incomingOffers', label: 'Incoming Offers', icon: Inbox },
  { id: 'availability', label: 'Availability', icon: Clock3 },
];

function getFriendlyError(error) {
  const status = error?.response?.status;
  const backendError = error?.response?.data?.error;
  const code = error?.response?.data?.code;

  if (status === 403 || code === 'ROLE_PERMISSION_DENIED') {
    return backendError || "You do not have permission for this Doctor's Circle action.";
  }
  if (status === 404) {
    return backendError || "Doctor's Circle profile was not found.";
  }
  if (status === 400) {
    return backendError || 'Please review the entered profile details and try again.';
  }
  if (status === 409) {
    return backendError || 'This profile action could not be completed because of the current profile state.';
  }

  return backendError || error?.message || 'Doctor Circle could not be reached. Please try again.';
}

function statusLabel(value) {
  if (!value) return 'Draft';
  return String(value).replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
}

function statusClasses(value) {
  const normalized = String(value || '').toLowerCase();
  if (normalized === 'published' || normalized === 'approved') {
    return 'border-emerald-200 bg-emerald-50 text-emerald-700';
  }
  if (normalized === 'paused' || normalized === 'pending') {
    return 'border-amber-200 bg-amber-50 text-amber-700';
  }
  if (normalized === 'rejected' || normalized === 'archived') {
    return 'border-rose-200 bg-rose-50 text-rose-700';
  }
  return 'border-slate-200 bg-slate-50 text-slate-700';
}

function formatCurrency(value) {
  const parsed = Number(value || 0);
  return `${RUPEE_SYMBOL}${Number.isFinite(parsed) ? parsed.toFixed(2) : '0.00'}`;
}

function getInitials(name) {
  const parts = String(name || 'Doctor')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2);
  return parts.map((part) => part.charAt(0).toUpperCase()).join('') || 'DC';
}

function getPublicCasePhotos(profile) {
  return Array.isArray(profile?.casePhotos)
    ? profile.casePhotos
        .filter((photo) => photo?.imageUrl)
        .slice(0, 5)
    : [];
}

function getPublicAvailability(profile) {
  return Array.isArray(profile?.availability)
    ? profile.availability.filter((row) => row?.isActive !== false)
    : [];
}

function sanitizePublicConsultantProfile(profile) {
  if (!profile) return null;
  return {
    id: profile.id,
    displayName: profile.displayName || '',
    degree: profile.degree || '',
    education: profile.education || '',
    specialization: profile.specialization || '',
    yearsOfExperience: Number(profile.yearsOfExperience || 0),
    areaOfAvailability: profile.areaOfAvailability || '',
    timeOfAvailability: profile.timeOfAvailability || '',
    feesPerService: Number(profile.feesPerService || 0),
    shortMessage: profile.shortMessage || '',
    profilePhotoUrl: profile.profilePhotoUrl || '',
    isOpenToWork: profile.isOpenToWork !== false,
    publicSlug: profile.publicSlug || '',
    casePhotos: getPublicCasePhotos(profile).map((photo) => ({
      id: photo.id,
      imageUrl: photo.imageUrl,
      photoType: photo.photoType || 'portfolio',
      sortOrder: photo.sortOrder || 0,
    })),
    availability: getPublicAvailability(profile).map((row) => ({
      id: row.id,
      areaName: row.areaName || '',
      availableDays: row.availableDays || null,
      startTime: row.startTime || '',
      endTime: row.endTime || '',
      notes: row.notes || '',
      isActive: row.isActive !== false,
    })),
  };
}

function hasContactLikeText(value) {
  const text = String(value || '');
  const emailPattern = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i;
  const urlPattern = /\b(?:https?:\/\/|www\.)\S+/i;
  const phonePattern = /(?:\+?\d[\s().-]*){8,}/;
  return emailPattern.test(text) || urlPattern.test(text) || phonePattern.test(text);
}

function buildPublicSafetyWarnings(form) {
  const checkedFields = [
    ['Display Name', form.displayName],
    ['Degree', form.degree],
    ['Education', form.education],
    ['Specialization', form.specialization],
    ['Area of Availability', form.areaOfAvailability],
    ['Time of Availability', form.timeOfAvailability],
    ['Short Public Message', form.shortMessage],
  ];

  return checkedFields
    .filter(([, value]) => hasContactLikeText(value))
    .map(([label]) => `${label} may contain contact details.`);
}

function buildOfferSafetyWarnings(form) {
  const checkedFields = [
    ['Safe Service Summary', form.safeServiceSummary],
    ['Short Message', form.shortMessage],
    ['Consultation Category', form.consultationCategory],
    ['Anonymous Clinic Area', form.anonymousClinicArea],
  ];

  return checkedFields
    .filter(([, value]) => hasContactLikeText(value))
    .map(([label]) => `${label} may contain phone, email, website, or contact details.`);
}

function formatDateTime(value) {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '-';
  return parsed.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function offerStatusClasses(value) {
  const normalized = String(value || '').toLowerCase();
  if (normalized === 'sent') return 'border-blue-200 bg-blue-50 text-blue-700';
  if (normalized === 'viewed') return 'border-indigo-200 bg-indigo-50 text-indigo-700';
  if (normalized === 'accepted' || normalized === 'unlocked') return 'border-emerald-200 bg-emerald-50 text-emerald-700';
  if (normalized === 'declined' || normalized === 'cancelled' || normalized === 'expired') return 'border-rose-200 bg-rose-50 text-rose-700';
  return 'border-slate-200 bg-slate-50 text-slate-700';
}

function canCancelOffer(offer) {
  return ['sent', 'viewed'].includes(String(offer?.status || '').toLowerCase());
}

function canRespondToOffer(offer) {
  return ['sent', 'viewed'].includes(String(offer?.status || '').toLowerCase());
}

function canOpenChatForOffer(offer) {
  return String(offer?.status || '').toLowerCase() === 'unlocked';
}

function reportEntityLabel(entityType) {
  const labels = {
    profile: 'consultant profile',
    case_photo: 'case photo',
    offer: 'consult offer',
    chat_thread: 'chat thread',
    chat_message: 'chat message',
    user: 'user',
  };
  return labels[entityType] || 'Doctor Circle item';
}

function sanitizeOfferConsultantProfile(profile) {
  if (!profile) return null;
  return {
    id: profile.id || null,
    displayName: profile.displayName || '',
    degree: profile.degree || '',
    education: profile.education || '',
    specialization: profile.specialization || '',
    yearsOfExperience: Number(profile.yearsOfExperience || 0),
    areaOfAvailability: profile.areaOfAvailability || '',
    timeOfAvailability: profile.timeOfAvailability || '',
    feesPerService: Number(profile.feesPerService || 0),
    profilePhotoUrl: profile.profilePhotoUrl || '',
    publicSlug: profile.publicSlug || '',
  };
}

function sanitizeSentOffer(offer) {
  if (!offer) return null;
  return {
    id: offer.id,
    appointmentDateTime: offer.appointmentDateTime || '',
    offeredAmount: Number(offer.offeredAmount || 0),
    shortMessage: offer.shortMessage || '',
    safeServiceSummary: offer.safeServiceSummary || '',
    consultationCategory: offer.consultationCategory || '',
    anonymousClinicArea: offer.anonymousClinicArea || '',
    status: offer.status || 'sent',
    viewedAt: offer.viewedAt || '',
    consultantResponseReason: offer.consultantResponseReason || '',
    consultantRespondedAt: offer.consultantRespondedAt || '',
    expiresAt: offer.expiresAt || '',
    cancelledAt: offer.cancelledAt || '',
    cancellationReason: offer.cancellationReason || '',
    createdAt: offer.createdAt || '',
    updatedAt: offer.updatedAt || '',
    consultantProfile: sanitizeOfferConsultantProfile(offer.consultantProfile),
  };
}

function sanitizeIncomingOffer(offer) {
  if (!offer) return null;
  return {
    id: offer.id,
    anonymousClinicLabel: offer.anonymousClinicLabel || 'Clinic request',
    appointmentDateTime: offer.appointmentDateTime || '',
    offeredAmount: Number(offer.offeredAmount || 0),
    shortMessage: offer.shortMessage || '',
    safeServiceSummary: offer.safeServiceSummary || '',
    consultationCategory: offer.consultationCategory || '',
    anonymousClinicArea: offer.anonymousClinicArea || '',
    status: offer.status || 'sent',
    viewedAt: offer.viewedAt || '',
    consultantResponseReason: offer.consultantResponseReason || '',
    consultantRespondedAt: offer.consultantRespondedAt || '',
    unlockPaymentRequired: Boolean(offer.unlockPaymentRequired),
    unlockPaymentMessage: offer.unlockPaymentMessage || '',
    expiresAt: offer.expiresAt || '',
    cancelledAt: offer.cancelledAt || '',
    cancellationReason: offer.cancellationReason || '',
    createdAt: offer.createdAt || '',
    updatedAt: offer.updatedAt || '',
    unlockPayment: sanitizeUnlockPayment(offer.unlockPayment || offer.payment || null),
    consultantProfile: sanitizeOfferConsultantProfile(offer.consultantProfile),
  };
}

function sanitizeUnlockPayment(payment) {
  if (!payment) return null;
  return {
    id: payment.id || null,
    offerId: payment.offerId || null,
    amount: Number(payment.amount || 0),
    currency: payment.currency || 'INR',
    paymentStatus: payment.paymentStatus || 'not_started',
    unlockStatus: payment.unlockStatus || 'locked',
    paymentProvider: payment.paymentProvider || 'gateway_pending',
    paidAt: payment.paidAt || '',
    failedAt: payment.failedAt || '',
    failureReason: payment.failureReason || '',
    unlockedAt: payment.unlockedAt || '',
    gatewayConfigured: Boolean(payment.gatewayConfigured),
    onlinePaymentAvailable: Boolean(payment.onlinePaymentAvailable),
    gatewayMessage: payment.gatewayMessage || 'Online unlock payment integration is pending.',
  };
}

function sanitizeChatThread(thread) {
  if (!thread) return null;
  return {
    id: thread.id || null,
    offerId: thread.offerId || null,
    status: thread.status || 'active',
    openedAt: thread.openedAt || '',
    closedAt: thread.closedAt || '',
    closeReason: thread.closeReason || '',
    lastMessageAt: thread.lastMessageAt || '',
    participantSide: thread.participantSide || '',
    counterpartyLabel: thread.counterpartyLabel || thread.anonymousClinicLabel || 'Doctor Circle chat',
    anonymousClinicLabel: thread.anonymousClinicLabel || '',
    consultantProfile: sanitizeOfferConsultantProfile(thread.consultantProfile),
    offer: thread.offer
      ? {
          id: thread.offer.id || null,
          appointmentDateTime: thread.offer.appointmentDateTime || '',
          offeredAmount: Number(thread.offer.offeredAmount || 0),
          shortMessage: thread.offer.shortMessage || '',
          safeServiceSummary: thread.offer.safeServiceSummary || '',
          consultationCategory: thread.offer.consultationCategory || '',
          anonymousClinicArea: thread.offer.anonymousClinicArea || '',
          status: thread.offer.status || '',
        }
      : null,
  };
}

function sanitizeChatMessage(message) {
  if (!message) return null;
  return {
    id: message.id || null,
    threadId: message.threadId || null,
    senderSide: message.senderSide || '',
    senderLabel: message.senderLabel || '',
    messageText: message.messageText || '',
    messageType: message.messageType || 'text',
    isRead: Boolean(message.isRead),
    readAt: message.readAt || '',
    createdAt: message.createdAt || '',
    updatedAt: message.updatedAt || '',
  };
}

function normalizeProfileToForm(profile) {
  if (!profile) return { ...EMPTY_PROFILE_FORM };
  return {
    displayName: profile.displayName || '',
    degree: profile.degree || '',
    education: profile.education || '',
    specialization: profile.specialization || '',
    yearsOfExperience:
      profile.yearsOfExperience === null || profile.yearsOfExperience === undefined
        ? ''
        : String(profile.yearsOfExperience),
    areaOfAvailability: profile.areaOfAvailability || '',
    timeOfAvailability: profile.timeOfAvailability || '',
    feesPerService:
      profile.feesPerService === null || profile.feesPerService === undefined
        ? ''
        : String(profile.feesPerService),
    shortMessage: profile.shortMessage || '',
    profilePhotoUrl: profile.profilePhotoUrl || '',
    isOpenToWork: profile.isOpenToWork !== false,
  };
}

function normalizeAvailabilityRows(rows) {
  if (!Array.isArray(rows) || rows.length === 0) {
    return [{ ...EMPTY_AVAILABILITY_ROW }];
  }

  return rows.map((row) => ({
    areaName: row.areaName || '',
    availableDaysText: Array.isArray(row.availableDays)
      ? row.availableDays.join(', ')
      : String(row.availableDays || ''),
    startTime: row.startTime || '',
    endTime: row.endTime || '',
    notes: row.notes || '',
    isActive: row.isActive !== false,
  }));
}

function buildProfilePayload(form) {
  return {
    displayName: form.displayName.trim(),
    degree: form.degree.trim() || null,
    education: form.education.trim() || null,
    specialization: form.specialization.trim() || null,
    yearsOfExperience: Number(form.yearsOfExperience || 0),
    areaOfAvailability: form.areaOfAvailability.trim() || null,
    timeOfAvailability: form.timeOfAvailability.trim() || null,
    feesPerService: Number(form.feesPerService || 0),
    shortMessage: form.shortMessage.trim() || null,
    profilePhotoUrl: form.profilePhotoUrl.trim() || null,
    isOpenToWork: Boolean(form.isOpenToWork),
  };
}

function buildAvailabilityPayload(rows) {
  return rows
    .map((row) => ({
      areaName: row.areaName.trim() || null,
      availableDays: row.availableDaysText
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean),
      startTime: row.startTime || null,
      endTime: row.endTime || null,
      notes: row.notes.trim() || null,
      isActive: row.isActive !== false,
    }))
    .filter(
      (row) =>
        row.areaName ||
        row.availableDays.length ||
        row.startTime ||
        row.endTime ||
        row.notes
    );
}

function Field({ label, children, warning }) {
  return (
    <div>
      <label className="label-modern">{label}</label>
      {children}
      {warning ? <p className="mt-2 text-xs font-medium text-amber-600">{warning}</p> : null}
    </div>
  );
}

function ProfileAvatar({ src, name, className = '', initialsClassName = 'text-lg' }) {
  const [imageFailed, setImageFailed] = useState(false);

  useEffect(() => {
    setImageFailed(false);
  }, [src]);

  return (
    <div className={`overflow-hidden border border-slate-200 bg-blue-50 ${className}`}>
      {src && !imageFailed ? (
        <img
          src={src}
          alt={name || 'Doctor Circle profile'}
          className="h-full w-full object-cover"
          onError={() => setImageFailed(true)}
        />
      ) : (
        <div className={`flex h-full w-full items-center justify-center font-black text-blue-700 ${initialsClassName}`}>
          {getInitials(name)}
        </div>
      )}
    </div>
  );
}

function PublicCasePhotoStrip({ photos = [], onReport }) {
  if (!photos.length) {
    return (
      <div className="rounded-[18px] border border-dashed border-slate-200 bg-slate-50 px-4 py-5 text-center text-sm text-slate-500">
        No public case photos added.
      </div>
    );
  }

  return (
    <div className="grid grid-cols-5 gap-2">
      {photos.slice(0, 5).map((photo) => (
        <div key={photo.id || photo.imageUrl} className="group relative aspect-square overflow-hidden rounded-[14px] border border-slate-200 bg-slate-50">
          <img src={photo.imageUrl} alt="Public case" className="h-full w-full object-cover" />
          {onReport && photo.id ? (
            <button
              type="button"
              className="absolute bottom-1 right-1 rounded-full border border-white/70 bg-slate-900/70 p-1.5 text-white opacity-0 transition group-hover:opacity-100"
              onClick={() => onReport('case_photo', photo.id, 'Case photo')}
              title="Report case photo"
            >
              <Flag className="h-3.5 w-3.5" />
            </button>
          ) : null}
        </div>
      ))}
    </div>
  );
}

function ConsultantCard({ consultant, onOpenDetails, onConsult, onReport, canCreateOffers }) {
  const casePhotos = getPublicCasePhotos(consultant);

  return (
    <article className="rounded-[24px] border border-slate-200 bg-white p-4 shadow-[0_10px_24px_rgba(15,23,42,0.04)]">
      <div className="flex flex-col gap-4 md:flex-row">
        <ProfileAvatar
          src={consultant.profilePhotoUrl}
          name={consultant.displayName || 'Consultant'}
          className="h-24 w-24 shrink-0 rounded-[18px]"
        />

        <div className="min-w-0 flex-1">
          <div className="flex flex-col gap-2 lg:flex-row lg:items-start lg:justify-between">
            <div className="min-w-0">
              <button
                type="button"
                onClick={() => onOpenDetails(consultant)}
                className="text-left text-lg font-bold tracking-tight text-blue-700 transition hover:text-blue-800"
              >
                {consultant.displayName || 'Consultant Doctor'}
              </button>
              {consultant.degree ? <p className="mt-0.5 text-sm font-semibold text-slate-500">{consultant.degree}</p> : null}
            </div>
            {consultant.isOpenToWork ? (
              <span className="inline-flex w-fit items-center rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
                Open to work
              </span>
            ) : null}
          </div>

          {consultant.specialization ? (
            <p className="mt-3 text-sm font-semibold text-slate-800">{consultant.specialization}</p>
          ) : null}
          {consultant.shortMessage ? (
            <p className="mt-2 line-clamp-2 text-sm leading-6 text-slate-600">{consultant.shortMessage}</p>
          ) : null}

          <div className="mt-4 grid grid-cols-1 gap-2 text-sm text-slate-600 md:grid-cols-2 xl:grid-cols-4">
            <div className="rounded-[16px] border border-slate-200 bg-slate-50 px-3 py-2">
              <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">Area</p>
              <p className="mt-1 font-semibold text-slate-800">{consultant.areaOfAvailability || '-'}</p>
            </div>
            <div className="rounded-[16px] border border-slate-200 bg-slate-50 px-3 py-2">
              <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">Time</p>
              <p className="mt-1 font-semibold text-slate-800">{consultant.timeOfAvailability || '-'}</p>
            </div>
            <div className="rounded-[16px] border border-slate-200 bg-slate-50 px-3 py-2">
              <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">Experience</p>
              <p className="mt-1 font-semibold text-slate-800">{Number(consultant.yearsOfExperience || 0)} years</p>
            </div>
            <div className="rounded-[16px] border border-slate-200 bg-slate-50 px-3 py-2">
              <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">Fees</p>
              <p className="mt-1 font-semibold text-slate-800">{formatCurrency(consultant.feesPerService)}</p>
            </div>
          </div>

          <div className="mt-4">
            <PublicCasePhotoStrip photos={casePhotos} onReport={onReport} />
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <button type="button" className="btn-secondary !rounded-xl !px-4 !py-2 !text-xs" onClick={() => onOpenDetails(consultant)}>
              <Eye className="h-3.5 w-3.5" />
              View Details
            </button>
            <button type="button" className="btn-secondary !rounded-xl !px-4 !py-2 !text-xs" onClick={() => onReport('profile', consultant.id, consultant.displayName || 'Consultant profile')}>
              <Flag className="h-3.5 w-3.5" />
              Report
            </button>
            <button
              type="button"
              className="btn-primary !rounded-xl !px-4 !py-2 !text-xs disabled:cursor-not-allowed disabled:opacity-60"
              onClick={() => onConsult(consultant)}
              disabled={!canCreateOffers}
              title={canCreateOffers ? 'Send consult request' : 'Only Owner/Admin and Doctor roles can send consult offers'}
            >
              <BriefcaseBusiness className="h-3.5 w-3.5" />
              Consult
            </button>
          </div>
        </div>
      </div>
    </article>
  );
}

function ConsultantDetailsModal({ consultant, loading, onClose, onConsult, onReport, canCreateOffers }) {
  if (!consultant && !loading) return null;

  const casePhotos = getPublicCasePhotos(consultant);
  const availability = getPublicAvailability(consultant);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/45 p-4 backdrop-blur-sm">
      <div className="max-h-[92vh] w-full max-w-4xl overflow-hidden rounded-[26px] border border-slate-200 bg-white shadow-[0_22px_70px_rgba(15,23,42,0.24)]">
        <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-5 py-4">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-blue-600">Public Consultant Profile</p>
            <h2 className="mt-2 text-xl font-bold tracking-tight text-slate-900">
              {loading ? 'Loading consultant...' : consultant?.displayName || 'Consultant Doctor'}
            </h2>
          </div>
          <button type="button" onClick={onClose} className="rounded-full border border-slate-200 bg-white p-2.5 text-slate-500 transition hover:text-slate-800">
            <X className="h-5 w-5" />
          </button>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 px-6 py-10 text-sm font-semibold text-slate-600">
            <Loader2 className="h-4 w-4 animate-spin text-indigo-600" />
            Loading public profile...
          </div>
        ) : (
          <div className="max-h-[calc(92vh-86px)] overflow-y-auto p-5">
            <div className="grid grid-cols-1 gap-5 lg:grid-cols-[180px_minmax(0,1fr)]">
              <div className="space-y-3">
                <ProfileAvatar
                  src={consultant.profilePhotoUrl}
                  name={consultant.displayName || 'Consultant'}
                  className="aspect-square rounded-[22px]"
                  initialsClassName="text-3xl"
                />
                {consultant.isOpenToWork ? (
                  <span className="inline-flex w-full items-center justify-center rounded-full border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-700">
                    Open to work
                  </span>
                ) : null}
              </div>

              <div>
                <h3 className="text-2xl font-bold tracking-tight text-blue-700">{consultant.displayName || 'Consultant Doctor'}</h3>
                {consultant.degree ? <p className="mt-1 text-sm font-semibold text-slate-500">{consultant.degree}</p> : null}
                {consultant.specialization ? <p className="mt-4 text-base font-semibold text-slate-900">{consultant.specialization}</p> : null}
                {consultant.education ? <p className="mt-2 text-sm leading-6 text-slate-600">{consultant.education}</p> : null}
                {consultant.shortMessage ? (
                  <div className="mt-4 rounded-[20px] border border-slate-200 bg-slate-50 px-4 py-3 text-sm leading-6 text-slate-700">
                    {consultant.shortMessage}
                  </div>
                ) : null}

                <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
                  <div className="rounded-[18px] border border-slate-200 bg-white px-4 py-3">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">Area</p>
                    <p className="mt-2 text-sm font-semibold text-slate-900">{consultant.areaOfAvailability || '-'}</p>
                  </div>
                  <div className="rounded-[18px] border border-slate-200 bg-white px-4 py-3">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">Time</p>
                    <p className="mt-2 text-sm font-semibold text-slate-900">{consultant.timeOfAvailability || '-'}</p>
                  </div>
                  <div className="rounded-[18px] border border-slate-200 bg-white px-4 py-3">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">Experience</p>
                    <p className="mt-2 text-sm font-semibold text-slate-900">{Number(consultant.yearsOfExperience || 0)} years</p>
                  </div>
                  <div className="rounded-[18px] border border-slate-200 bg-white px-4 py-3">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">Fees</p>
                    <p className="mt-2 text-sm font-semibold text-slate-900">{formatCurrency(consultant.feesPerService)}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 grid grid-cols-1 gap-5 lg:grid-cols-2">
              <section className="rounded-[22px] border border-slate-200 bg-white p-4">
                <h4 className="text-sm font-semibold text-slate-900">Public Case Photos</h4>
                <div className="mt-3">
                  <PublicCasePhotoStrip photos={casePhotos} onReport={onReport} />
                </div>
              </section>

              <section className="rounded-[22px] border border-slate-200 bg-white p-4">
                <h4 className="text-sm font-semibold text-slate-900">Availability</h4>
                {availability.length ? (
                  <div className="mt-3 space-y-2">
                    {availability.map((row) => (
                      <div key={row.id || `${row.areaName}-${row.startTime}`} className="rounded-[16px] border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
                        <p className="font-semibold text-slate-900">{row.areaName || consultant.areaOfAvailability || 'Available area'}</p>
                        <p className="mt-1">
                          {Array.isArray(row.availableDays) && row.availableDays.length ? row.availableDays.join(', ') : 'Days not specified'}
                          {row.startTime || row.endTime ? ` | ${row.startTime || '-'} - ${row.endTime || '-'}` : ''}
                        </p>
                        {row.notes ? <p className="mt-1 text-slate-500">{row.notes}</p> : null}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="mt-3 rounded-[18px] border border-dashed border-slate-200 bg-slate-50 px-4 py-5 text-center text-sm text-slate-500">
                    Detailed availability has not been added.
                  </p>
                )}
              </section>
            </div>

            <div className="mt-6 flex flex-wrap items-center justify-end gap-3 border-t border-slate-100 pt-4">
              <button type="button" className="btn-secondary" onClick={onClose}>
                Close
              </button>
              <button type="button" className="btn-secondary" onClick={() => onReport('profile', consultant.id, consultant.displayName || 'Consultant profile')}>
                <Flag className="h-4 w-4" />
                Report
              </button>
              <button type="button" className="btn-primary disabled:cursor-not-allowed disabled:opacity-60" onClick={() => onConsult(consultant)} disabled={!canCreateOffers}>
                <BriefcaseBusiness className="h-4 w-4" />
                Consult
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ConsultOfferModal({
  consultant,
  form,
  warnings,
  sending,
  onClose,
  onChange,
  onSubmit,
}) {
  if (!consultant) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/45 p-4 backdrop-blur-sm">
      <div className="max-h-[92vh] w-full max-w-3xl overflow-hidden rounded-[26px] border border-slate-200 bg-white shadow-[0_22px_70px_rgba(15,23,42,0.24)]">
        <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-5 py-4">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-blue-600">Consult Offer</p>
            <h2 className="mt-2 text-xl font-bold tracking-tight text-slate-900">
              Send request to {consultant.displayName || 'Consultant Doctor'}
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              Dektal will notify the consultant safely. Chat remains locked until the consultant accepts and completes unlock payment.
            </p>
          </div>
          <button type="button" onClick={onClose} className="rounded-full border border-slate-200 bg-white p-2.5 text-slate-500 transition hover:text-slate-800">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={onSubmit} className="max-h-[calc(92vh-88px)] overflow-y-auto p-5">
          <div className="rounded-[20px] border border-amber-200 bg-amber-50 px-4 py-3 text-sm leading-6 text-amber-800">
            <p className="font-semibold">Clinic name, contact details, and patient details are hidden from the consultant until the unlock/payment flow.</p>
            <p className="mt-1">Do not type patient name, phone number, clinic contact number, or private information inside the short message.</p>
          </div>

          {warnings.length ? (
            <div className="mt-4 rounded-[18px] border border-amber-200 bg-white px-4 py-3 text-sm text-amber-700">
              <p className="font-semibold">Safety warning</p>
              <ul className="mt-2 list-disc space-y-1 pl-5">
                {warnings.map((warning) => (
                  <li key={warning}>{warning}</li>
                ))}
              </ul>
            </div>
          ) : null}

          <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2">
            <Field label="Patient Appointment Date/Time *">
              <input
                className="input-modern"
                type="datetime-local"
                value={form.appointmentDateTime}
                onChange={(event) => onChange('appointmentDateTime', event.target.value)}
              />
            </Field>
            <Field label="Offered Amount *">
              <input
                className="input-modern"
                type="number"
                min="1"
                step="0.01"
                value={form.offeredAmount}
                onChange={(event) => onChange('offeredAmount', event.target.value)}
                placeholder="2500"
              />
            </Field>
            <Field label="Consultation Category">
              <input
                className="input-modern"
                value={form.consultationCategory}
                onChange={(event) => onChange('consultationCategory', event.target.value)}
                placeholder="Endodontics, implant consultation"
                maxLength={255}
              />
            </Field>
            <Field label="Approximate Clinic Area">
              <input
                className="input-modern"
                value={form.anonymousClinicArea}
                onChange={(event) => onChange('anonymousClinicArea', event.target.value)}
                placeholder="General area only, no clinic address"
                maxLength={255}
              />
            </Field>
          </div>

          <div className="mt-4 grid grid-cols-1 gap-4">
            <Field label="Safe Service Summary">
              <textarea
                className="input-modern min-h-[96px]"
                value={form.safeServiceSummary}
                onChange={(event) => onChange('safeServiceSummary', event.target.value)}
                placeholder="General service requirement only. Do not include patient identity or clinic contact."
                maxLength={1000}
              />
            </Field>
            <Field label="Short Message">
              <textarea
                className="input-modern min-h-[96px]"
                value={form.shortMessage}
                onChange={(event) => onChange('shortMessage', event.target.value)}
                placeholder="Optional message without patient names, contact numbers, email, clinic address, or private notes."
                maxLength={1000}
              />
            </Field>
          </div>

          <div className="mt-5 flex flex-wrap items-center justify-end gap-3 border-t border-slate-100 pt-4">
            <button type="button" className="btn-secondary" onClick={onClose} disabled={sending}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={sending}>
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              Send Consult Request
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function OfferCard({
  offer,
  type,
  onView,
  onCancel,
  onMarkViewed,
  onAccept,
  onDecline,
  onUnlock,
  onOpenChat,
  onReport,
  actionLoading,
}) {
  const consultantName = offer.consultantProfile?.displayName || 'Consultant Doctor';
  const heading = type === 'incoming' ? offer.anonymousClinicLabel || 'Clinic request' : consultantName;
  const subheading = type === 'incoming'
    ? 'Clinic identity and patient details are hidden before unlock.'
    : offer.consultantProfile?.specialization || offer.consultantProfile?.degree || 'Doctor Circle consultant';

  return (
    <article className="rounded-[22px] border border-slate-200 bg-white p-4 shadow-[0_10px_24px_rgba(15,23,42,0.04)]">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-lg font-bold text-slate-900">{heading}</h3>
            <span className={`rounded-full border px-3 py-1 text-xs font-semibold ${offerStatusClasses(offer.status)}`}>
              {statusLabel(offer.status)}
            </span>
          </div>
          <p className="mt-1 text-sm font-semibold text-slate-500">{subheading}</p>
          <div className="mt-4 grid grid-cols-1 gap-2 text-sm md:grid-cols-3">
            <div className="rounded-[16px] border border-slate-200 bg-slate-50 px-3 py-2">
              <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">Appointment</p>
              <p className="mt-1 font-semibold text-slate-800">{formatDateTime(offer.appointmentDateTime)}</p>
            </div>
            <div className="rounded-[16px] border border-slate-200 bg-slate-50 px-3 py-2">
              <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">Offered Amount</p>
              <p className="mt-1 font-semibold text-slate-800">{formatCurrency(offer.offeredAmount)}</p>
            </div>
            <div className="rounded-[16px] border border-slate-200 bg-slate-50 px-3 py-2">
              <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">Created</p>
              <p className="mt-1 font-semibold text-slate-800">{formatDateTime(offer.createdAt)}</p>
            </div>
          </div>
          {offer.safeServiceSummary || offer.shortMessage ? (
            <p className="mt-3 line-clamp-2 text-sm leading-6 text-slate-600">
              {offer.safeServiceSummary || offer.shortMessage}
            </p>
          ) : null}
          {type === 'incoming' && offer.status === 'accepted' ? (
            <p className="mt-3 rounded-[16px] border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-700">
              {offer.unlockPaymentMessage || 'Offer accepted. Unlock payment is required before clinic contact/chat is available.'}
            </p>
          ) : null}
          {type === 'incoming' && offer.status === 'unlocked' ? (
            <p className="mt-3 rounded-[16px] border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-700">
              Unlock payment recorded. Private chat is available. Patient details are not shared automatically.
            </p>
          ) : null}
          {type === 'incoming' && offer.unlockPayment ? (
            <p className="mt-2 text-xs font-semibold text-slate-500">
              Unlock payment: {statusLabel(offer.unlockPayment.paymentStatus)} / {statusLabel(offer.unlockPayment.unlockStatus)}
            </p>
          ) : null}
          {type === 'incoming' && offer.status === 'declined' ? (
            <p className="mt-3 rounded-[16px] border border-rose-200 bg-rose-50 px-3 py-2 text-sm font-semibold text-rose-700">
              You declined this offer. Reason: {offer.consultantResponseReason || 'Reason not available.'}
            </p>
          ) : null}
          {type === 'sent' && offer.status === 'accepted' ? (
            <p className="mt-3 rounded-[16px] border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-700">
              Consultant accepted. Unlock/payment stage pending.
            </p>
          ) : null}
          {type === 'sent' && offer.status === 'unlocked' ? (
            <p className="mt-3 rounded-[16px] border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-700">
              Consultant unlock payment recorded. Private chat is available. Contact reveal remains controlled.
            </p>
          ) : null}
          {type === 'sent' && offer.status === 'declined' ? (
            <p className="mt-3 rounded-[16px] border border-rose-200 bg-rose-50 px-3 py-2 text-sm font-semibold text-rose-700">
              Declined reason: {offer.consultantResponseReason || 'Reason not available.'}
            </p>
          ) : null}
        </div>

        <div className="flex shrink-0 flex-wrap gap-2">
          <button type="button" className="btn-secondary !rounded-xl !px-3 !py-2 !text-xs" onClick={() => onView(offer, type)}>
            <Eye className="h-3.5 w-3.5" />
            View Details
          </button>
          <button type="button" className="btn-secondary !rounded-xl !px-3 !py-2 !text-xs" onClick={() => onReport('offer', offer.id, `${type === 'incoming' ? 'Incoming' : 'Sent'} offer`)}>
            <Flag className="h-3.5 w-3.5" />
            Report
          </button>
          {type === 'sent' && canCancelOffer(offer) ? (
            <button type="button" className="inline-flex items-center gap-2 rounded-xl border border-rose-200 bg-white px-3 py-2 text-xs font-semibold text-rose-600 transition hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-60" onClick={() => onCancel(offer)} disabled={actionLoading === `cancel-${offer.id}`}>
              {actionLoading === `cancel-${offer.id}` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <X className="h-3.5 w-3.5" />}
              Cancel
            </button>
          ) : null}
          {type === 'incoming' && offer.status === 'sent' ? (
            <button type="button" className="btn-secondary !rounded-xl !px-3 !py-2 !text-xs" onClick={() => onMarkViewed(offer)} disabled={actionLoading === `viewed-${offer.id}`}>
              {actionLoading === `viewed-${offer.id}` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
              Mark Viewed
            </button>
          ) : null}
          {type === 'incoming' && canRespondToOffer(offer) ? (
            <>
              <button type="button" className="inline-flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-600 px-3 py-2 text-xs font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60" onClick={() => onAccept(offer)} disabled={actionLoading === `accept-${offer.id}`}>
                {actionLoading === `accept-${offer.id}` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
                Accept
              </button>
              <button type="button" className="inline-flex items-center gap-2 rounded-xl border border-rose-200 bg-white px-3 py-2 text-xs font-semibold text-rose-600 transition hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-60" onClick={() => onDecline(offer)} disabled={actionLoading === `decline-${offer.id}`}>
                {actionLoading === `decline-${offer.id}` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <X className="h-3.5 w-3.5" />}
                Decline
              </button>
            </>
          ) : null}
          {type === 'incoming' && offer.status === 'accepted' ? (
            <button type="button" className="inline-flex items-center gap-2 rounded-xl border border-blue-200 bg-blue-600 px-3 py-2 text-xs font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60" onClick={() => onUnlock(offer)} disabled={actionLoading === `unlock-${offer.id}`}>
              {actionLoading === `unlock-${offer.id}` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <BriefcaseBusiness className="h-3.5 w-3.5" />}
              Continue to Unlock
            </button>
          ) : null}
          {canOpenChatForOffer(offer) ? (
            <button type="button" className="inline-flex items-center gap-2 rounded-xl border border-indigo-200 bg-indigo-600 px-3 py-2 text-xs font-semibold text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60" onClick={() => onOpenChat(offer)} disabled={actionLoading === `chat-${offer.id}`}>
              {actionLoading === `chat-${offer.id}` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <MessageCircle className="h-3.5 w-3.5" />}
              Open Chat
            </button>
          ) : null}
        </div>
      </div>
    </article>
  );
}

function OfferDetailModal({
  offer,
  type,
  onClose,
  onCancel,
  onMarkViewed,
  onAccept,
  onDecline,
  onUnlock,
  onOpenChat,
  onReport,
  actionLoading,
}) {
  if (!offer) return null;

  const isIncoming = type === 'incoming';

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/45 p-4 backdrop-blur-sm">
      <div className="max-h-[92vh] w-full max-w-3xl overflow-hidden rounded-[26px] border border-slate-200 bg-white shadow-[0_22px_70px_rgba(15,23,42,0.24)]">
        <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-5 py-4">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-blue-600">
              {isIncoming ? 'Incoming Offer' : 'Sent Offer'}
            </p>
            <h2 className="mt-2 text-xl font-bold tracking-tight text-slate-900">
              {isIncoming ? offer.anonymousClinicLabel || 'Clinic request' : offer.consultantProfile?.displayName || 'Consultant Doctor'}
            </h2>
          </div>
          <button type="button" onClick={onClose} className="rounded-full border border-slate-200 bg-white p-2.5 text-slate-500 transition hover:text-slate-800">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="max-h-[calc(92vh-88px)] overflow-y-auto p-5">
          {isIncoming ? (
            <div className="rounded-[20px] border border-blue-100 bg-blue-50 px-4 py-3 text-sm leading-6 text-blue-800">
              This is a privacy-safe view. Clinic contact details, patient identity, patient contact, tenant IDs, appointment IDs, internal notes, and Consultant Fees Ledger data are not shown before unlock/payment.
            </div>
          ) : (
            <div className="rounded-[20px] border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm leading-6 text-emerald-800">
              Clinic-side view is limited to your own sent offer and the consultant's public profile summary.
            </div>
          )}

          {isIncoming && offer.status === 'accepted' ? (
            <div className="mt-4 rounded-[20px] border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-semibold leading-6 text-emerald-800">
              {offer.unlockPaymentMessage || 'Offer accepted. Unlock payment is required before clinic contact/chat is available.'}
            </div>
          ) : null}
          {isIncoming && offer.status === 'unlocked' ? (
            <div className="mt-4 rounded-[20px] border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-semibold leading-6 text-emerald-800">
              Unlock payment recorded. Private chat is available. Patient details are not shared automatically.
            </div>
          ) : null}
          {isIncoming && offer.status === 'declined' ? (
            <div className="mt-4 rounded-[20px] border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-semibold leading-6 text-rose-700">
              You declined this offer. Reason: {offer.consultantResponseReason || 'Reason not available.'}
            </div>
          ) : null}
          {!isIncoming && offer.status === 'accepted' ? (
            <div className="mt-4 rounded-[20px] border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-semibold leading-6 text-emerald-800">
              Consultant accepted. Unlock/payment stage pending.
            </div>
          ) : null}
          {!isIncoming && offer.status === 'unlocked' ? (
            <div className="mt-4 rounded-[20px] border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-semibold leading-6 text-emerald-800">
              Consultant unlock payment recorded. Private chat is available. Contact reveal remains controlled.
            </div>
          ) : null}
          {!isIncoming && offer.status === 'declined' ? (
            <div className="mt-4 rounded-[20px] border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-semibold leading-6 text-rose-700">
              Consultant declined this offer. Reason: {offer.consultantResponseReason || 'Reason not available.'}
            </div>
          ) : null}

          <div className="mt-5 grid grid-cols-1 gap-3 md:grid-cols-2">
            <div className="rounded-[18px] border border-slate-200 bg-white px-4 py-3">
              <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">Status</p>
              <span className={`mt-2 inline-flex rounded-full border px-3 py-1 text-xs font-semibold ${offerStatusClasses(offer.status)}`}>
                {statusLabel(offer.status)}
              </span>
            </div>
            <div className="rounded-[18px] border border-slate-200 bg-white px-4 py-3">
              <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">Appointment Date/Time</p>
              <p className="mt-2 text-sm font-semibold text-slate-900">{formatDateTime(offer.appointmentDateTime)}</p>
            </div>
            <div className="rounded-[18px] border border-slate-200 bg-white px-4 py-3">
              <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">Offered Amount</p>
              <p className="mt-2 text-sm font-semibold text-slate-900">{formatCurrency(offer.offeredAmount)}</p>
            </div>
            <div className="rounded-[18px] border border-slate-200 bg-white px-4 py-3">
              <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">Expires</p>
              <p className="mt-2 text-sm font-semibold text-slate-900">{formatDateTime(offer.expiresAt)}</p>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-1 gap-4">
            {offer.consultationCategory ? (
              <div className="rounded-[18px] border border-slate-200 bg-slate-50 px-4 py-3">
                <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">Consultation Category</p>
                <p className="mt-2 text-sm font-semibold text-slate-900">{offer.consultationCategory}</p>
              </div>
            ) : null}
            {offer.anonymousClinicArea ? (
              <div className="rounded-[18px] border border-slate-200 bg-slate-50 px-4 py-3">
                <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">Approximate Area</p>
                <p className="mt-2 text-sm font-semibold text-slate-900">{offer.anonymousClinicArea}</p>
              </div>
            ) : null}
            <div className="rounded-[18px] border border-slate-200 bg-slate-50 px-4 py-3">
              <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">Safe Service Summary</p>
              <p className="mt-2 text-sm leading-6 text-slate-700">{offer.safeServiceSummary || '-'}</p>
            </div>
            <div className="rounded-[18px] border border-slate-200 bg-slate-50 px-4 py-3">
              <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">Short Message</p>
              <p className="mt-2 text-sm leading-6 text-slate-700">{offer.shortMessage || '-'}</p>
            </div>
            {offer.cancellationReason ? (
              <div className="rounded-[18px] border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700">
                <p className="text-[10px] font-semibold uppercase tracking-[0.14em]">Cancellation Reason</p>
                <p className="mt-2 text-sm leading-6">{offer.cancellationReason}</p>
              </div>
            ) : null}
            {!isIncoming && offer.consultantResponseReason ? (
              <div className="rounded-[18px] border border-slate-200 bg-white px-4 py-3">
                <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">Consultant Response</p>
                <p className="mt-2 text-sm leading-6 text-slate-700">{offer.consultantResponseReason}</p>
              </div>
            ) : null}
            {isIncoming && offer.unlockPayment ? (
              <div className="rounded-[18px] border border-blue-200 bg-blue-50 px-4 py-3 text-blue-800">
                <p className="text-[10px] font-semibold uppercase tracking-[0.14em]">Unlock Payment</p>
                <p className="mt-2 text-sm font-semibold">
                  {formatCurrency(offer.unlockPayment.amount || 2500)} {offer.unlockPayment.currency || 'INR'} - {statusLabel(offer.unlockPayment.paymentStatus)}
                </p>
                <p className="mt-1 text-sm leading-6">
                  {offer.unlockPayment.gatewayMessage || 'Online unlock payment integration is pending.'}
                </p>
              </div>
            ) : null}
          </div>

          {isIncoming && canRespondToOffer(offer) ? (
            <div className="mt-5 rounded-[20px] border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              Accepting does not reveal clinic contact details or patient details. Chat stays locked until successful unlock payment.
            </div>
          ) : null}

          <div className="mt-5 flex flex-wrap items-center justify-end gap-3 border-t border-slate-100 pt-4">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Close
            </button>
            <button type="button" className="btn-secondary" onClick={() => onReport('offer', offer.id, isIncoming ? 'Incoming offer' : 'Sent offer')}>
              <Flag className="h-4 w-4" />
              Report
            </button>
            {isIncoming && offer.status === 'sent' ? (
              <button type="button" className="btn-secondary" onClick={() => onMarkViewed(offer)} disabled={actionLoading === `viewed-${offer.id}`}>
                {actionLoading === `viewed-${offer.id}` ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                Mark Viewed
              </button>
            ) : null}
            {isIncoming && canRespondToOffer(offer) ? (
              <>
                <button type="button" className="inline-flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60" onClick={() => onAccept(offer)} disabled={actionLoading === `accept-${offer.id}`}>
                  {actionLoading === `accept-${offer.id}` ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                  Accept Offer
                </button>
                <button type="button" className="inline-flex items-center gap-2 rounded-xl border border-rose-200 bg-white px-4 py-2.5 text-sm font-semibold text-rose-600 transition hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-60" onClick={() => onDecline(offer)} disabled={actionLoading === `decline-${offer.id}`}>
                  {actionLoading === `decline-${offer.id}` ? <Loader2 className="h-4 w-4 animate-spin" /> : <X className="h-4 w-4" />}
                  Decline Offer
                </button>
              </>
            ) : null}
            {isIncoming && offer.status === 'accepted' ? (
              <button type="button" className="inline-flex items-center gap-2 rounded-xl border border-blue-200 bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60" onClick={() => onUnlock(offer)} disabled={actionLoading === `unlock-${offer.id}`}>
                {actionLoading === `unlock-${offer.id}` ? <Loader2 className="h-4 w-4 animate-spin" /> : <BriefcaseBusiness className="h-4 w-4" />}
                Continue to Unlock
              </button>
            ) : null}
            {canOpenChatForOffer(offer) ? (
              <button type="button" className="inline-flex items-center gap-2 rounded-xl border border-indigo-200 bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60" onClick={() => onOpenChat(offer)} disabled={actionLoading === `chat-${offer.id}`}>
                {actionLoading === `chat-${offer.id}` ? <Loader2 className="h-4 w-4 animate-spin" /> : <MessageCircle className="h-4 w-4" />}
                Open Chat
              </button>
            ) : null}
            {!isIncoming && canCancelOffer(offer) ? (
              <button type="button" className="inline-flex items-center gap-2 rounded-xl border border-rose-200 bg-white px-4 py-2.5 text-sm font-semibold text-rose-600 transition hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-60" onClick={() => onCancel(offer)} disabled={actionLoading === `cancel-${offer.id}`}>
                {actionLoading === `cancel-${offer.id}` ? <Loader2 className="h-4 w-4 animate-spin" /> : <X className="h-4 w-4" />}
                Cancel Offer
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

function ChatModal({
  thread,
  messages,
  value,
  loading,
  sending,
  error,
  onChange,
  onClose,
  onRefresh,
  onReport,
  onSubmit,
}) {
  if (!thread) return null;

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-900/45 p-4 backdrop-blur-sm">
      <div className="flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-[26px] border border-slate-200 bg-white shadow-[0_22px_70px_rgba(15,23,42,0.24)]">
        <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-5 py-4">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-indigo-600">Doctor's Circle Chat</p>
            <h2 className="mt-2 text-xl font-bold tracking-tight text-slate-900">
              {thread.counterpartyLabel || 'Private chat'}
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              Chat is available only after successful unlock payment. Patient details are not inserted automatically.
            </p>
          </div>
          <button type="button" onClick={onClose} className="rounded-full border border-slate-200 bg-white p-2.5 text-slate-500 transition hover:text-slate-800">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="border-b border-slate-100 bg-white px-5 py-3">
          <button type="button" className="btn-secondary !rounded-xl !px-3 !py-2 !text-xs" onClick={() => onReport('chat_thread', thread.id, thread.counterpartyLabel || 'Doctor Circle chat thread')} disabled={!thread.id}>
            <Flag className="h-3.5 w-3.5" />
            Report Chat
          </button>
        </div>

        <div className="border-b border-amber-100 bg-amber-50 px-5 py-3 text-sm leading-6 text-amber-800">
          Do not type patient names, patient phone numbers, unnecessary clinic contact information, or sensitive private notes unless your clinic intentionally chooses to share them.
        </div>

        {error ? (
          <div className="border-b border-rose-100 bg-rose-50 px-5 py-3 text-sm font-semibold text-rose-700">
            {error}
          </div>
        ) : null}

        <div className="min-h-[320px] flex-1 overflow-y-auto bg-slate-50 px-5 py-4">
          {loading ? (
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-600">
              <Loader2 className="h-4 w-4 animate-spin text-indigo-600" />
              Loading chat messages...
            </div>
          ) : messages.length ? (
            <div className="space-y-3">
              {messages.map((message) => {
                const isMine = message.senderLabel === 'You';
                return (
                  <div key={message.id || `${message.createdAt}-${message.senderSide}`} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[82%] rounded-[18px] border px-4 py-3 ${isMine ? 'border-indigo-200 bg-indigo-600 text-white' : 'border-slate-200 bg-white text-slate-800'}`}>
                      <div className={`mb-1 text-[11px] font-semibold uppercase tracking-[0.14em] ${isMine ? 'text-indigo-100' : 'text-slate-400'}`}>
                        {message.senderLabel || statusLabel(message.senderSide)}
                      </div>
                      <p className="whitespace-pre-wrap break-words text-sm leading-6">{message.messageText}</p>
                      <p className={`mt-2 text-[11px] ${isMine ? 'text-indigo-100' : 'text-slate-400'}`}>
                        {formatDateTime(message.createdAt)}
                      </p>
                      {message.id ? (
                        <button
                          type="button"
                          className={`mt-2 inline-flex items-center gap-1 text-[11px] font-semibold ${isMine ? 'text-indigo-100' : 'text-slate-500'}`}
                          onClick={() => onReport('chat_message', message.id, 'Chat message')}
                        >
                          <Flag className="h-3 w-3" />
                          Report
                        </button>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="rounded-[20px] border border-dashed border-slate-200 bg-white px-5 py-8 text-center text-sm text-slate-500">
              No messages yet. Start with a short, privacy-safe message.
            </div>
          )}
        </div>

        <form className="border-t border-slate-100 bg-white p-4" onSubmit={onSubmit}>
          <div className="flex flex-col gap-3 md:flex-row md:items-end">
            <div className="flex-1">
              <label className="label-modern">Message</label>
              <textarea
                className="input-modern min-h-[92px] resize-y"
                value={value}
                onChange={(event) => onChange(event.target.value)}
                maxLength={2000}
                placeholder="Type a privacy-safe chat message"
              />
              <p className="mt-1 text-xs font-semibold text-slate-400">{value.length}/2000</p>
            </div>
            <div className="flex gap-2">
              <button type="button" className="btn-secondary" onClick={onRefresh} disabled={loading || sending}>
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                Refresh
              </button>
              <button type="submit" className="btn-primary" disabled={sending || !value.trim()}>
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                Send
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

function ReportModal({
  context,
  form,
  submitting,
  onChange,
  onClose,
  onSubmit,
}) {
  if (!context) return null;
  const warningText = hasContactLikeText(form.reasonText)
    ? 'The report note appears to contain phone, email, website, or contact details. Please remove them before submitting.'
    : '';

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-slate-900/45 p-4 backdrop-blur-sm">
      <div className="w-full max-w-xl rounded-[26px] border border-slate-200 bg-white shadow-[0_22px_70px_rgba(15,23,42,0.24)]">
        <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-5 py-4">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-rose-600">Report Doctor's Circle</p>
            <h2 className="mt-2 text-xl font-bold tracking-tight text-slate-900">
              Report {context.label || reportEntityLabel(context.reportedEntityType)}
            </h2>
          </div>
          <button type="button" onClick={onClose} className="rounded-full border border-slate-200 bg-white p-2.5 text-slate-500 transition hover:text-slate-800" disabled={submitting}>
            <X className="h-5 w-5" />
          </button>
        </div>

        <form className="space-y-4 p-5" onSubmit={onSubmit}>
          <div className="rounded-[20px] border border-amber-200 bg-amber-50 px-4 py-3 text-sm leading-6 text-amber-800">
            Reports are reviewed safely. Do not type patient names, patient phone numbers, clinic contact numbers, or private clinical information in the report note.
          </div>

          <Field label="Reason type">
            <select className="input-modern" value={form.reasonType} onChange={(event) => onChange('reasonType', event.target.value)}>
              {REPORT_REASON_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </Field>

          <Field label="Optional note" warning={warningText}>
            <textarea
              className="textarea-modern min-h-[120px]"
              value={form.reasonText}
              onChange={(event) => onChange('reasonText', event.target.value)}
              maxLength={1000}
              placeholder="Short report note without contact details or patient information."
            />
          </Field>

          <div className="flex flex-wrap items-center justify-end gap-3 border-t border-slate-100 pt-4">
            <button type="button" className="btn-secondary" onClick={onClose} disabled={submitting}>
              Cancel
            </button>
            <button type="submit" className="inline-flex items-center gap-2 rounded-xl border border-rose-200 bg-rose-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-60" disabled={submitting || Boolean(warningText)}>
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Flag className="h-4 w-4" />}
              Submit Report
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function DeclineOfferModal({
  offer,
  form,
  onChange,
  onClose,
  onSubmit,
  submitting,
}) {
  if (!offer) return null;
  const isOther = form.reasonType === 'Other';
  const finalReason = isOther ? form.otherReason.trim() : form.reasonType;
  const warningText = hasContactLikeText(finalReason)
    ? 'The decline reason appears to contain phone, email, website, or contact details. Please remove them before declining.'
    : '';

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/45 p-4 backdrop-blur-sm">
      <div className="w-full max-w-xl rounded-[26px] border border-slate-200 bg-white shadow-[0_22px_70px_rgba(15,23,42,0.24)]">
        <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-5 py-4">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-rose-600">Decline Offer</p>
            <h2 className="mt-2 text-xl font-bold tracking-tight text-slate-900">
              Decline {offer.anonymousClinicLabel || 'clinic request'}
            </h2>
          </div>
          <button type="button" onClick={onClose} className="rounded-full border border-slate-200 bg-white p-2.5 text-slate-500 transition hover:text-slate-800" disabled={submitting}>
            <X className="h-5 w-5" />
          </button>
        </div>

        <form className="space-y-4 p-5" onSubmit={onSubmit}>
          <div className="rounded-[20px] border border-amber-200 bg-amber-50 px-4 py-3 text-sm leading-6 text-amber-800">
            Decline reason is required and will be sent back to the clinic. Do not enter patient names, phone numbers, clinic contact numbers, or private information.
          </div>

          <div>
            <label className="field-label" htmlFor="doctor-circle-decline-reason">
              Reason
            </label>
            <select
              id="doctor-circle-decline-reason"
              className="input-field"
              value={form.reasonType}
              onChange={(event) => onChange('reasonType', event.target.value)}
              disabled={submitting}
              required
            >
              {DECLINE_REASON_OPTIONS.map((reason) => (
                <option key={reason} value={reason}>
                  {reason}
                </option>
              ))}
            </select>
          </div>

          {isOther ? (
            <div>
              <label className="field-label" htmlFor="doctor-circle-decline-other">
                Other Reason
              </label>
              <textarea
                id="doctor-circle-decline-other"
                className="input-field min-h-[110px]"
                value={form.otherReason}
                onChange={(event) => onChange('otherReason', event.target.value)}
                maxLength={1000}
                disabled={submitting}
                required
                placeholder="Enter a short reason without contact or patient details."
              />
              <p className="mt-1 text-xs text-slate-400">{form.otherReason.length}/1000</p>
            </div>
          ) : null}

          {warningText ? (
            <div className="flex items-start gap-2 rounded-[18px] border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{warningText}</span>
            </div>
          ) : null}

          <div className="flex flex-wrap items-center justify-end gap-3 border-t border-slate-100 pt-4">
            <button type="button" className="btn-secondary" onClick={onClose} disabled={submitting}>
              Keep Offer
            </button>
            <button type="submit" className="inline-flex items-center gap-2 rounded-xl border border-rose-200 bg-rose-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-60" disabled={submitting || Boolean(warningText)}>
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <X className="h-4 w-4" />}
              Decline Offer
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function RestrictedManagementPanel() {
  return (
    <section className="section-card p-6">
      <div className="flex items-start gap-4">
        <div className="rounded-2xl bg-amber-50 p-3 text-amber-600">
          <ShieldAlert className="h-6 w-6" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Profile management is restricted</h2>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-500">
            Your role can use Doctor's Circle search if the backend permits it, but public consultant profile management is limited to Owner/Admin, Doctor, and future Consultant Doctor roles.
          </p>
        </div>
      </div>
    </section>
  );
}

export default function DoctorsCircle() {
  const user = useAuthStore((state) => state.user);
  const roleKey = normalizeRole(user?.role);
  const canManageProfile = PROFILE_MANAGER_ROLES.includes(roleKey);
  const canSearchConsultants = SEARCH_ACCESS_ROLES.includes(roleKey);
  const canCreateConsultOffers = CONSULT_OFFER_CREATOR_ROLES.includes(roleKey);
  const canViewIncomingOffers = CONSULT_OFFER_VIEWER_ROLES.includes(roleKey);
  const [activeTab, setActiveTab] = useState('findConsultants');
  const [profile, setProfile] = useState(null);
  const [profileForm, setProfileForm] = useState({ ...EMPTY_PROFILE_FORM });
  const [casePhotoForm, setCasePhotoForm] = useState({ ...EMPTY_CASE_PHOTO_FORM });
  const [availabilityRows, setAvailabilityRows] = useState([{ ...EMPTY_AVAILABILITY_ROW }]);
  const [searchFilters, setSearchFilters] = useState({ ...EMPTY_SEARCH_FILTERS });
  const [consultants, setConsultants] = useState([]);
  const [consultantPagination, setConsultantPagination] = useState({ limit: 20, offset: 0, total: 0 });
  const [consultantsLoading, setConsultantsLoading] = useState(false);
  const [consultantsError, setConsultantsError] = useState('');
  const [selectedConsultant, setSelectedConsultant] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [offerModalConsultant, setOfferModalConsultant] = useState(null);
  const [offerForm, setOfferForm] = useState({ ...EMPTY_OFFER_FORM });
  const [offerSending, setOfferSending] = useState(false);
  const [sentOffers, setSentOffers] = useState([]);
  const [sentOffersPagination, setSentOffersPagination] = useState({ limit: 20, offset: 0, total: 0 });
  const [sentOffersLoading, setSentOffersLoading] = useState(false);
  const [sentOffersError, setSentOffersError] = useState('');
  const [incomingOffers, setIncomingOffers] = useState([]);
  const [incomingOffersPagination, setIncomingOffersPagination] = useState({ limit: 20, offset: 0, total: 0 });
  const [incomingOffersLoading, setIncomingOffersLoading] = useState(false);
  const [incomingOffersError, setIncomingOffersError] = useState('');
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [selectedOfferType, setSelectedOfferType] = useState('');
  const [offerActionLoading, setOfferActionLoading] = useState('');
  const [declineModalOffer, setDeclineModalOffer] = useState(null);
  const [declineForm, setDeclineForm] = useState({ ...EMPTY_DECLINE_FORM });
  const [chatThread, setChatThread] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatText, setChatText] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [chatSending, setChatSending] = useState(false);
  const [chatError, setChatError] = useState('');
  const [reportContext, setReportContext] = useState(null);
  const [reportForm, setReportForm] = useState({ ...EMPTY_REPORT_FORM });
  const [reportSubmitting, setReportSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingError, setLoadingError] = useState('');
  const [savingAction, setSavingAction] = useState('');
  const [selectedProfilePhoto, setSelectedProfilePhoto] = useState(null);
  const [profilePhotoPreviewUrl, setProfilePhotoPreviewUrl] = useState('');
  const [profilePhotoError, setProfilePhotoError] = useState('');
  const [profilePhotoUploading, setProfilePhotoUploading] = useState(false);
  const [profilePhotoRemoving, setProfilePhotoRemoving] = useState(false);
  const [profilePhotoProgress, setProfilePhotoProgress] = useState(0);
  const profilePhotoInputRef = useRef(null);
  const profilePhotoUploadLockRef = useRef(false);
  const [selectedCasePhoto, setSelectedCasePhoto] = useState(null);
  const [casePhotoPreviewUrl, setCasePhotoPreviewUrl] = useState('');
  const [casePhotoError, setCasePhotoError] = useState('');
  const [casePhotoUploading, setCasePhotoUploading] = useState(false);
  const [casePhotoProgress, setCasePhotoProgress] = useState(0);
  const casePhotoInputRef = useRef(null);
  const casePhotoUploadLockRef = useRef(false);

  const warnings = useMemo(() => buildPublicSafetyWarnings(profileForm), [profileForm]);
  const offerWarnings = useMemo(() => buildOfferSafetyWarnings(offerForm), [offerForm]);
  const casePhotos = Array.isArray(profile?.casePhotos) ? profile.casePhotos : [];
  const activeCasePhotoCount = casePhotos.filter((photo) => photo?.isActive !== false && !photo?.archivedAt).length;
  const canAddCasePhoto = activeCasePhotoCount < 5;

  const searchResultsSummary = consultantPagination.total
    ? `${consultantPagination.total} public consultant${consultantPagination.total === 1 ? '' : 's'} found`
    : 'No public consultants found yet';
  const isProfileWorkspaceActive = PROFILE_WORKSPACE_TAB_IDS.includes(activeTab);

  const loadProfile = useCallback(async () => {
    if (!canManageProfile) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setLoadingError('');
    try {
      const nextProfile = await getMyDoctorCircleProfile();
      setProfile(nextProfile);
      setProfileForm(normalizeProfileToForm(nextProfile));
      setAvailabilityRows(normalizeAvailabilityRows(nextProfile?.availability));
    } catch (error) {
      const message = getFriendlyError(error);
      setLoadingError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [canManageProfile]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  useEffect(() => () => {
    if (profilePhotoPreviewUrl) {
      URL.revokeObjectURL?.(profilePhotoPreviewUrl);
    }
  }, [profilePhotoPreviewUrl]);

  useEffect(() => () => {
    if (casePhotoPreviewUrl) {
      URL.revokeObjectURL?.(casePhotoPreviewUrl);
    }
  }, [casePhotoPreviewUrl]);

  const runConsultantSearch = useCallback(
    async ({ offset = 0, quiet = false } = {}) => {
      if (!canSearchConsultants) return;

      if (!quiet) {
        setConsultantsLoading(true);
      }
      setConsultantsError('');

      try {
        const minExperience = Number(searchFilters.minimumYearsExperience || 0);
        const payload = await searchPublishedDoctorCircleProfiles({
          q: searchFilters.search.trim() || undefined,
          education: searchFilters.education.trim() || undefined,
          specialization: searchFilters.specialization.trim() || undefined,
          area: searchFilters.area.trim() || undefined,
          availability: searchFilters.availability.trim() || undefined,
          openToWork: searchFilters.openToWork ? 'true' : undefined,
          sortBy: searchFilters.sortBy,
          limit: 20,
          offset,
        });
        const safeRows = payload.data
          .map(sanitizePublicConsultantProfile)
          .filter(Boolean)
          .filter((row) => Number(row.yearsOfExperience || 0) >= minExperience);

        setConsultants(safeRows);
        setConsultantPagination(payload.pagination || { limit: 20, offset, total: safeRows.length });
      } catch (error) {
        const message = getFriendlyError(error);
        setConsultantsError(message);
        toast.error(message);
      } finally {
        setConsultantsLoading(false);
      }
    },
    [canSearchConsultants, searchFilters]
  );

  useEffect(() => {
    if (activeTab === 'findConsultants') {
      runConsultantSearch({ offset: 0, quiet: true });
    }
  }, [activeTab, runConsultantSearch]);

  const updateSearchFilter = (field, value) => {
    setSearchFilters((current) => ({ ...current, [field]: value }));
  };

  const submitSearch = (event) => {
    event?.preventDefault?.();
    runConsultantSearch({ offset: 0 });
  };

  const clearSearch = async () => {
    setSearchFilters({ ...EMPTY_SEARCH_FILTERS });
    setConsultantPagination({ limit: 20, offset: 0, total: 0 });
    setConsultantsLoading(true);
    setConsultantsError('');

    try {
      const payload = await searchPublishedDoctorCircleProfiles({
        openToWork: 'true',
        sortBy: EMPTY_SEARCH_FILTERS.sortBy,
        limit: 20,
        offset: 0,
      });
      setConsultants(payload.data.map(sanitizePublicConsultantProfile).filter(Boolean));
      setConsultantPagination(payload.pagination || { limit: 20, offset: 0, total: 0 });
    } catch (error) {
      const message = getFriendlyError(error);
      setConsultantsError(message);
      toast.error(message);
    } finally {
      setConsultantsLoading(false);
    }
  };

  const openConsultantDetails = async (consultant) => {
    setSelectedConsultant(consultant);
    setDetailsLoading(true);

    try {
      const detailProfile = await getPublishedDoctorCircleProfile(consultant.publicSlug || consultant.id);
      setSelectedConsultant(sanitizePublicConsultantProfile(detailProfile) || consultant);
    } catch (error) {
      toast.error(getFriendlyError(error));
    } finally {
      setDetailsLoading(false);
    }
  };

  const updateOfferForm = (field, value) => {
    setOfferForm((current) => ({ ...current, [field]: value }));
  };

  const openConsultOfferModal = (consultant) => {
    if (!canCreateConsultOffers) {
      toast.error('Only Owner/Admin and Doctor roles can send Doctor Circle consult offers.');
      return;
    }
    setOfferModalConsultant(consultant);
    setOfferForm({ ...EMPTY_OFFER_FORM });
  };

  const closeConsultOfferModal = () => {
    if (offerSending) return;
    setOfferModalConsultant(null);
    setOfferForm({ ...EMPTY_OFFER_FORM });
  };

  const loadSentOffers = useCallback(
    async ({ offset = 0, quiet = false } = {}) => {
      if (!canCreateConsultOffers) return;
      if (!quiet) setSentOffersLoading(true);
      setSentOffersError('');

      try {
        const payload = await getMyDoctorCircleSentOffers({ limit: 20, offset });
        const safeRows = payload.data.map(sanitizeSentOffer).filter(Boolean);
        setSentOffers(safeRows);
        setSentOffersPagination(payload.pagination || { limit: 20, offset, total: safeRows.length });
      } catch (error) {
        const message = getFriendlyError(error);
        setSentOffersError(message);
        toast.error(message);
      } finally {
        setSentOffersLoading(false);
      }
    },
    [canCreateConsultOffers]
  );

  const loadIncomingOffers = useCallback(
    async ({ offset = 0, quiet = false } = {}) => {
      if (!canViewIncomingOffers) return;
      if (!quiet) setIncomingOffersLoading(true);
      setIncomingOffersError('');

      try {
        const payload = await getMyDoctorCircleIncomingOffers({ limit: 20, offset });
        const safeRows = payload.data.map(sanitizeIncomingOffer).filter(Boolean);
        setIncomingOffers(safeRows);
        setIncomingOffersPagination(payload.pagination || { limit: 20, offset, total: safeRows.length });
      } catch (error) {
        const message = getFriendlyError(error);
        setIncomingOffersError(message);
        toast.error(message);
      } finally {
        setIncomingOffersLoading(false);
      }
    },
    [canViewIncomingOffers]
  );

  useEffect(() => {
    if (activeTab === 'sentOffers') {
      loadSentOffers({ offset: 0, quiet: true });
    }
  }, [activeTab, loadSentOffers]);

  useEffect(() => {
    if (activeTab === 'incomingOffers') {
      loadIncomingOffers({ offset: 0, quiet: true });
    }
  }, [activeTab, loadIncomingOffers]);

  const submitConsultOffer = async (event) => {
    event?.preventDefault?.();
    if (!offerModalConsultant?.id) {
      toast.error('Select a consultant before sending an offer.');
      return;
    }
    if (!offerForm.appointmentDateTime) {
      toast.error('Patient appointment date/time is required.');
      return;
    }
    const appointmentDate = new Date(offerForm.appointmentDateTime);
    if (Number.isNaN(appointmentDate.getTime()) || appointmentDate.getTime() <= Date.now()) {
      toast.error('Appointment date/time must be in the future.');
      return;
    }
    if (!offerForm.offeredAmount || Number(offerForm.offeredAmount) <= 0) {
      toast.error('Offered amount must be greater than 0.');
      return;
    }
    if (offerForm.shortMessage.length > 1000 || offerForm.safeServiceSummary.length > 1000) {
      toast.error('Message and service summary must be 1000 characters or less.');
      return;
    }

    setOfferSending(true);
    try {
      await createDoctorCircleConsultOffer({
        consultantProfileId: offerModalConsultant.id,
        appointmentDateTime: offerForm.appointmentDateTime,
        offeredAmount: Number(offerForm.offeredAmount),
        safeServiceSummary: offerForm.safeServiceSummary.trim() || undefined,
        shortMessage: offerForm.shortMessage.trim() || undefined,
        consultationCategory: offerForm.consultationCategory.trim() || undefined,
        anonymousClinicArea: offerForm.anonymousClinicArea.trim() || undefined,
      });
      toast.success('Consult request sent safely. Clinic contact details will stay hidden until unlock flow.');
      setOfferModalConsultant(null);
      setOfferForm({ ...EMPTY_OFFER_FORM });
      setActiveTab('sentOffers');
      await loadSentOffers({ offset: 0, quiet: true });
    } catch (error) {
      toast.error(getFriendlyError(error));
    } finally {
      setOfferSending(false);
    }
  };

  const openOfferDetails = async (offer, type) => {
    setSelectedOffer(offer);
    setSelectedOfferType(type);

    try {
      const result = await getDoctorCircleOfferById(offer.id);
      const detail = result?.offer || result;
      const accessSide = result?.accessSide || type;
      const isIncomingDetail = accessSide === 'consultant' || type === 'incoming';
      let safeDetail = isIncomingDetail ? sanitizeIncomingOffer(detail) : sanitizeSentOffer(detail);
      if (isIncomingDetail && ['accepted', 'unlocked'].includes(String(safeDetail?.status || '').toLowerCase())) {
        try {
          const unlockStatus = await getDoctorCircleUnlockPaymentStatus(offer.id);
          safeDetail = {
            ...safeDetail,
            unlockPayment: sanitizeUnlockPayment(unlockStatus?.payment),
          };
        } catch (_unlockStatusError) {
          safeDetail = {
            ...safeDetail,
            unlockPayment: null,
          };
        }
      }
      setSelectedOffer(safeDetail);
      setSelectedOfferType(accessSide === 'consultant' ? 'incoming' : type);
    } catch (error) {
      toast.error(getFriendlyError(error));
    }
  };

  const cancelOffer = async (offer) => {
    const confirmed = window.confirm('Cancel this Doctor Circle consult offer? The row will remain in history.');
    if (!confirmed) return;

    const reason = window.prompt('Optional cancellation reason. Do not enter clinic contact details or patient information.', '') || '';
    if (hasContactLikeText(reason)) {
      toast.error('Cancellation reason appears to contain contact details. Please remove them before cancelling.');
      return;
    }

    setOfferActionLoading(`cancel-${offer.id}`);
    try {
      const cancelledOffer = await cancelDoctorCircleConsultOffer(offer.id, reason.trim() || undefined);
      const safeOffer = sanitizeSentOffer(cancelledOffer);
      setSentOffers((current) => current.map((row) => (row.id === safeOffer.id ? safeOffer : row)));
      if (selectedOffer?.id === safeOffer.id) {
        setSelectedOffer(safeOffer);
        setSelectedOfferType('sent');
      }
      toast.success('Consult offer cancelled.');
      await loadSentOffers({ offset: Number(sentOffersPagination.offset || 0), quiet: true });
    } catch (error) {
      toast.error(getFriendlyError(error));
    } finally {
      setOfferActionLoading('');
    }
  };

  const markIncomingOfferViewed = async (offer) => {
    setOfferActionLoading(`viewed-${offer.id}`);
    try {
      const viewedOffer = await markDoctorCircleOfferViewed(offer.id);
      const safeOffer = sanitizeIncomingOffer(viewedOffer);
      setIncomingOffers((current) => current.map((row) => (row.id === safeOffer.id ? safeOffer : row)));
      if (selectedOffer?.id === safeOffer.id) {
        setSelectedOffer(safeOffer);
        setSelectedOfferType('incoming');
      }
      toast.success('Incoming offer marked as viewed.');
      await loadIncomingOffers({ offset: Number(incomingOffersPagination.offset || 0), quiet: true });
    } catch (error) {
      toast.error(getFriendlyError(error));
    } finally {
      setOfferActionLoading('');
    }
  };

  const acceptIncomingOffer = async (offer) => {
    const confirmed = window.confirm(
      'Accept this Doctor Circle consult offer? Clinic contact details and patient details will still stay hidden until unlock/payment.'
    );
    if (!confirmed) return;

    setOfferActionLoading(`accept-${offer.id}`);
    try {
      const acceptedOffer = await acceptDoctorCircleConsultOffer(offer.id);
      const safeOffer = sanitizeIncomingOffer(acceptedOffer);
      setIncomingOffers((current) => current.map((row) => (row.id === safeOffer.id ? safeOffer : row)));
      if (selectedOffer?.id === safeOffer.id) {
        setSelectedOffer(safeOffer);
        setSelectedOfferType('incoming');
      }
      toast.success('Offer accepted. Unlock payment is required before clinic contact/chat is available.');
      await loadIncomingOffers({ offset: Number(incomingOffersPagination.offset || 0), quiet: true });
    } catch (error) {
      toast.error(getFriendlyError(error));
    } finally {
      setOfferActionLoading('');
    }
  };

  const openDeclineOfferModal = (offer) => {
    setDeclineModalOffer(offer);
    setDeclineForm({ ...EMPTY_DECLINE_FORM });
  };

  const closeDeclineOfferModal = () => {
    if (offerActionLoading.startsWith('decline-')) return;
    setDeclineModalOffer(null);
    setDeclineForm({ ...EMPTY_DECLINE_FORM });
  };

  const updateDeclineForm = (field, value) => {
    setDeclineForm((current) => ({ ...current, [field]: value }));
  };

  const submitDeclineOffer = async (event) => {
    event?.preventDefault?.();
    if (!declineModalOffer?.id) {
      toast.error('Select an incoming offer before declining.');
      return;
    }

    const reasonType = declineForm.reasonType;
    const reason = reasonType === 'Other' ? declineForm.otherReason.trim() : reasonType;
    if (!reason) {
      toast.error('Decline reason is required.');
      return;
    }
    if (hasContactLikeText(reason)) {
      toast.error('Decline reason appears to contain contact details. Please remove them before declining.');
      return;
    }

    setOfferActionLoading(`decline-${declineModalOffer.id}`);
    try {
      const declinedOffer = await declineDoctorCircleConsultOffer(declineModalOffer.id, {
        reasonType,
        reason,
      });
      const safeOffer = sanitizeIncomingOffer(declinedOffer);
      setIncomingOffers((current) => current.map((row) => (row.id === safeOffer.id ? safeOffer : row)));
      if (selectedOffer?.id === safeOffer.id) {
        setSelectedOffer(safeOffer);
        setSelectedOfferType('incoming');
      }
      toast.success('Offer declined with reason.');
      setDeclineModalOffer(null);
      setDeclineForm({ ...EMPTY_DECLINE_FORM });
      await loadIncomingOffers({ offset: Number(incomingOffersPagination.offset || 0), quiet: true });
    } catch (error) {
      toast.error(getFriendlyError(error));
    } finally {
      setOfferActionLoading('');
    }
  };

  const initiateUnlockPayment = async (offer) => {
    if (String(offer?.status || '').toLowerCase() !== 'accepted') {
      toast.error('Only accepted offers can start unlock payment.');
      return;
    }

    setOfferActionLoading(`unlock-${offer.id}`);
    try {
      const result = await initiateDoctorCircleUnlockPayment(offer.id);
      const safePayment = sanitizeUnlockPayment(result?.payment);
      setIncomingOffers((current) =>
        current.map((row) => (row.id === offer.id ? { ...row, unlockPayment: safePayment } : row))
      );
      if (selectedOffer?.id === offer.id) {
        setSelectedOffer((current) => ({ ...current, unlockPayment: safePayment }));
        setSelectedOfferType('incoming');
      }
      toast.success(result?.message || 'Unlock payment foundation started. Online payment integration is pending.');
    } catch (error) {
      toast.error(getFriendlyError(error));
    } finally {
      setOfferActionLoading('');
    }
  };

  const loadChatMessages = async (threadId) => {
    if (!threadId) return;
    setChatLoading(true);
    setChatError('');

    try {
      const payload = await getDoctorCircleChatMessages(threadId, { limit: 100, offset: 0 });
      const safeThread = sanitizeChatThread(payload.thread) || chatThread;
      const safeMessages = payload.data.map(sanitizeChatMessage).filter(Boolean);
      if (safeThread) {
        setChatThread(safeThread);
      }
      setChatMessages(safeMessages);
      await markDoctorCircleChatThreadRead(threadId);
    } catch (error) {
      const message = getFriendlyError(error);
      setChatError(message);
      toast.error(message);
    } finally {
      setChatLoading(false);
    }
  };

  const openChatForOffer = async (offer) => {
    if (!canOpenChatForOffer(offer)) {
      toast.error("Chat unlocks after successful Doctor's Circle unlock payment.");
      return;
    }

    setOfferActionLoading(`chat-${offer.id}`);
    setChatLoading(true);
    setChatError('');
    setChatText('');
    setChatMessages([]);
    setChatThread({
      id: null,
      offerId: offer.id,
      status: 'active',
      counterpartyLabel: offer.anonymousClinicLabel || offer.consultantProfile?.displayName || 'Doctor Circle chat',
    });

    try {
      const thread = await openDoctorCircleChatForOffer(offer.id);
      const safeThread = sanitizeChatThread(thread);
      setChatThread(safeThread);
      if (safeThread?.id) {
        const payload = await getDoctorCircleChatMessages(safeThread.id, { limit: 100, offset: 0 });
        setChatThread(sanitizeChatThread(payload.thread) || safeThread);
        setChatMessages(payload.data.map(sanitizeChatMessage).filter(Boolean));
        await markDoctorCircleChatThreadRead(safeThread.id);
      }
      toast.success('Doctor Circle private chat opened.');
    } catch (error) {
      const message = getFriendlyError(error);
      setChatError(message);
      toast.error(message);
    } finally {
      setChatLoading(false);
      setOfferActionLoading('');
    }
  };

  const closeChatModal = () => {
    if (chatSending) return;
    setChatThread(null);
    setChatMessages([]);
    setChatText('');
    setChatError('');
  };

  const refreshChatMessages = () => {
    if (chatThread?.id) {
      loadChatMessages(chatThread.id);
    }
  };

  const submitChatMessage = async (event) => {
    event?.preventDefault?.();
    if (!chatThread?.id) {
      toast.error('Open an unlocked Doctor Circle chat before sending a message.');
      return;
    }
    const messageText = chatText.trim();
    if (!messageText) {
      toast.error('Message cannot be empty.');
      return;
    }
    if (messageText.length > 2000) {
      toast.error('Message must be 2000 characters or less.');
      return;
    }

    setChatSending(true);
    setChatError('');
    try {
      const result = await sendDoctorCircleChatMessage(chatThread.id, messageText);
      const safeThread = sanitizeChatThread(result?.thread) || chatThread;
      const safeMessage = sanitizeChatMessage(result?.message);
      setChatThread(safeThread);
      if (safeMessage) {
        setChatMessages((current) => [...current, safeMessage]);
      }
      setChatText('');
      toast.success('Message sent.');
    } catch (error) {
      const message = getFriendlyError(error);
      setChatError(message);
      toast.error(message);
    } finally {
      setChatSending(false);
    }
  };

  const openReportModal = (reportedEntityType, reportedEntityId, label = '') => {
    if (!reportedEntityType || !reportedEntityId) {
      toast.error('Select a Doctor Circle item before reporting.');
      return;
    }
    setReportContext({
      reportedEntityType,
      reportedEntityId,
      label: label || reportEntityLabel(reportedEntityType),
    });
    setReportForm({ ...EMPTY_REPORT_FORM });
  };

  const closeReportModal = () => {
    if (reportSubmitting) return;
    setReportContext(null);
    setReportForm({ ...EMPTY_REPORT_FORM });
  };

  const updateReportForm = (field, value) => {
    setReportForm((current) => ({ ...current, [field]: value }));
  };

  const submitReport = async (event) => {
    event?.preventDefault?.();
    if (!reportContext?.reportedEntityType || !reportContext?.reportedEntityId) {
      toast.error('Select a Doctor Circle item before reporting.');
      return;
    }
    if (!reportForm.reasonType) {
      toast.error('Report reason type is required.');
      return;
    }
    if (hasContactLikeText(reportForm.reasonText)) {
      toast.error('Report note appears to contain contact details. Please remove them before submitting.');
      return;
    }

    setReportSubmitting(true);
    try {
      await createDoctorCircleReport({
        reportedEntityType: reportContext.reportedEntityType,
        reportedEntityId: reportContext.reportedEntityId,
        reasonType: reportForm.reasonType,
        reasonText: reportForm.reasonText.trim() || undefined,
      });
      toast.success("Your Doctor's Circle report was submitted safely.");
      setReportContext(null);
      setReportForm({ ...EMPTY_REPORT_FORM });
    } catch (error) {
      toast.error(getFriendlyError(error));
    } finally {
      setReportSubmitting(false);
    }
  };

  const updateProfileForm = (field, value) => {
    setProfileForm((current) => ({ ...current, [field]: value }));
  };

  const saveProfile = async ({ publishAfterSave = false } = {}) => {
    if (!profileForm.displayName.trim()) {
      toast.error('Display Name is required.');
      return null;
    }
    if (Number(profileForm.yearsOfExperience || 0) < 0) {
      toast.error('Years of Experience cannot be negative.');
      return null;
    }
    if (Number(profileForm.feesPerService || 0) < 0) {
      toast.error('Fees Per Service cannot be negative.');
      return null;
    }

    if (warnings.length > 0) {
      toast.error('Please remove contact details before saving a public profile.');
      return null;
    }

    setSavingAction(publishAfterSave ? 'publish' : 'save');
    try {
      const savedProfile = await saveMyDoctorCircleProfile(buildProfilePayload(profileForm), Boolean(profile?.id));
      setProfile(savedProfile);
      setProfileForm(normalizeProfileToForm(savedProfile));
      toast.success(publishAfterSave ? 'Profile saved. Publishing now...' : 'Doctor Circle profile saved.');
      return savedProfile;
    } catch (error) {
      toast.error(getFriendlyError(error));
      return null;
    } finally {
      if (!publishAfterSave) {
        setSavingAction('');
      }
    }
  };

  const clearSelectedProfilePhoto = () => {
    setSelectedProfilePhoto(null);
    setProfilePhotoPreviewUrl('');
    setProfilePhotoProgress(0);
    if (profilePhotoInputRef.current) {
      profilePhotoInputRef.current.value = '';
    }
  };

  const selectProfilePhoto = (event) => {
    const file = event.target.files?.[0] || null;
    setProfilePhotoError('');
    if (!file) return;

    const extension = String(file.name || '').split('.').pop().toLowerCase();
    if (!PROFILE_PHOTO_TYPES.has(file.type) || !PROFILE_PHOTO_EXTENSIONS.has(extension)) {
      setProfilePhotoError('Profile photo must be a JPEG, PNG, or WebP image.');
      event.target.value = '';
      return;
    }
    if (file.size > PROFILE_PHOTO_MAX_BYTES) {
      setProfilePhotoError('Profile photo must be 5 MB or smaller.');
      event.target.value = '';
      return;
    }

    setSelectedProfilePhoto(file);
    setProfilePhotoPreviewUrl(URL.createObjectURL(file));
    setProfilePhotoProgress(0);
  };

  const uploadProfilePhoto = async () => {
    if (!selectedProfilePhoto || profilePhotoUploadLockRef.current) return;
    profilePhotoUploadLockRef.current = true;
    setProfilePhotoUploading(true);
    setProfilePhotoProgress(0);
    setProfilePhotoError('');

    try {
      let currentProfile = profile;
      if (!currentProfile?.id) {
        currentProfile = await saveProfile();
        if (!currentProfile?.id) return;
      }

      const result = await uploadMyDoctorCircleProfilePhoto(selectedProfilePhoto, (progressEvent) => {
        if (!progressEvent.total) return;
        setProfilePhotoProgress(Math.min(100, Math.round((progressEvent.loaded * 100) / progressEvent.total)));
      });
      const updatedProfile = result.profile || { ...currentProfile, profilePhotoUrl: result.photoUrl };
      setProfile(updatedProfile);
      setProfileForm(normalizeProfileToForm(updatedProfile));
      clearSelectedProfilePhoto();
      toast.success('Doctor Circle profile photo updated.');
      if (canSearchConsultants) {
        await runConsultantSearch({ offset: consultantPagination.offset, quiet: true });
      }
    } catch (error) {
      const message = getFriendlyError(error);
      setProfilePhotoError(message);
      toast.error(message);
    } finally {
      profilePhotoUploadLockRef.current = false;
      setProfilePhotoUploading(false);
    }
  };

  const removeProfilePhoto = async () => {
    if (profilePhotoRemoving || profilePhotoUploading || !profile?.profilePhotoUrl) return;
    if (!window.confirm("Remove your Doctor's Circle profile photo?")) return;

    setProfilePhotoRemoving(true);
    setProfilePhotoError('');
    try {
      const result = await removeMyDoctorCircleProfilePhoto();
      const updatedProfile = result.profile || { ...profile, profilePhotoUrl: '', profilePhotoPath: '' };
      setProfile(updatedProfile);
      setProfileForm(normalizeProfileToForm(updatedProfile));
      clearSelectedProfilePhoto();
      toast.success('Doctor Circle profile photo removed.');
      if (canSearchConsultants) {
        await runConsultantSearch({ offset: consultantPagination.offset, quiet: true });
      }
    } catch (error) {
      const message = getFriendlyError(error);
      setProfilePhotoError(message);
      toast.error(message);
    } finally {
      setProfilePhotoRemoving(false);
    }
  };

  const publishProfile = async () => {
    const savedProfile = await saveProfile({ publishAfterSave: true });
    if (!savedProfile) {
      setSavingAction('');
      return;
    }

    try {
      const publishedProfile = await publishMyDoctorCircleProfile();
      setProfile(publishedProfile);
      setProfileForm(normalizeProfileToForm(publishedProfile));
      toast.success('Doctor Circle profile submitted for publication.');
    } catch (error) {
      toast.error(getFriendlyError(error));
    } finally {
      setSavingAction('');
    }
  };

  const pauseProfile = async () => {
    setSavingAction('pause');
    try {
      const pausedProfile = await pauseMyDoctorCircleProfile();
      setProfile(pausedProfile);
      setProfileForm(normalizeProfileToForm(pausedProfile));
      toast.success('Doctor Circle profile paused.');
    } catch (error) {
      toast.error(getFriendlyError(error));
    } finally {
      setSavingAction('');
    }
  };

  const archiveProfile = async () => {
    const confirmed = window.confirm(
      "Archive your Doctor's Circle public consultant profile? This hides it and keeps the record archived."
    );
    if (!confirmed) return;

    setSavingAction('archive');
    try {
      const archivedProfile = await archiveMyDoctorCircleProfile('Archived from Doctor Circle profile page');
      setProfile(archivedProfile);
      setProfileForm(normalizeProfileToForm(archivedProfile));
      toast.success('Doctor Circle profile archived.');
    } catch (error) {
      toast.error(getFriendlyError(error));
    } finally {
      setSavingAction('');
    }
  };

  const clearSelectedCasePhoto = () => {
    setSelectedCasePhoto(null);
    setCasePhotoPreviewUrl('');
    setCasePhotoProgress(0);
    if (casePhotoInputRef.current) casePhotoInputRef.current.value = '';
  };

  const selectCasePhoto = (event) => {
    const file = event.target.files?.[0] || null;
    setCasePhotoError('');
    if (!file) return;
    const extension = String(file.name || '').split('.').pop().toLowerCase();
    if (!PROFILE_PHOTO_TYPES.has(file.type) || !PROFILE_PHOTO_EXTENSIONS.has(extension)) {
      setCasePhotoError('Case photo must be a JPEG, PNG, or WebP image.');
      event.target.value = '';
      return;
    }
    if (file.size <= 0) {
      setCasePhotoError('Case photo file cannot be empty.');
      event.target.value = '';
      return;
    }
    if (file.size > PROFILE_PHOTO_MAX_BYTES) {
      setCasePhotoError('Case photo must be 5 MB or smaller.');
      event.target.value = '';
      return;
    }
    setSelectedCasePhoto(file);
    setCasePhotoPreviewUrl(URL.createObjectURL(file));
    setCasePhotoProgress(0);
  };

  const addCasePhoto = async () => {
    if (!profile?.id) {
      toast.error('Save your consultant profile before adding case photos.');
      return;
    }
    if (!selectedCasePhoto) {
      setCasePhotoError('Choose a case photo to upload.');
      return;
    }
    if (!casePhotoForm.containsNoTextOrNumbersAttested) {
      toast.error('Confirm the case photo safety attestation before saving.');
      return;
    }
    if (!canAddCasePhoto) {
      toast.error('Maximum 5 active case photos are allowed.');
      return;
    }

    if (casePhotoUploadLockRef.current) return;
    casePhotoUploadLockRef.current = true;
    setCasePhotoUploading(true);
    setCasePhotoProgress(0);
    setCasePhotoError('');
    try {
      const uploadedPhoto = await uploadDoctorCircleCasePhoto(
        selectedCasePhoto,
        {
          photoType: casePhotoForm.photoType,
          containsNoTextOrNumbersAttested: true,
        },
        (progressEvent) => {
          if (!progressEvent.total) return;
          setCasePhotoProgress(Math.min(100, Math.round((progressEvent.loaded * 100) / progressEvent.total)));
        }
      );
      setProfile((current) => ({
        ...current,
        casePhotos: [...(Array.isArray(current?.casePhotos) ? current.casePhotos : []), uploadedPhoto],
      }));
      setCasePhotoForm({ ...EMPTY_CASE_PHOTO_FORM });
      clearSelectedCasePhoto();
      toast.success('Case photo uploaded and submitted for moderation.');
    } catch (error) {
      const message = getFriendlyError(error);
      setCasePhotoError(message);
      toast.error(message);
    } finally {
      casePhotoUploadLockRef.current = false;
      setCasePhotoUploading(false);
    }
  };

  const archiveCasePhoto = async (photo) => {
    const confirmed = window.confirm('Archive this Doctor Circle case photo?');
    if (!confirmed) return;

    setSavingAction(`case-photo-${photo.id}`);
    try {
      await archiveDoctorCircleCasePhoto(photo.id, 'Archived from Doctor Circle profile page');
      setProfile((current) => ({
        ...current,
        casePhotos: (current?.casePhotos || []).filter((item) => item.id !== photo.id),
      }));
      toast.success('Case photo archived.');
    } catch (error) {
      toast.error(getFriendlyError(error));
    } finally {
      setSavingAction('');
    }
  };

  const moveCasePhoto = async (index, direction) => {
    const nextIndex = index + direction;
    if (nextIndex < 0 || nextIndex >= casePhotos.length) return;

    const nextPhotos = [...casePhotos];
    [nextPhotos[index], nextPhotos[nextIndex]] = [nextPhotos[nextIndex], nextPhotos[index]];
    const photoOrders = nextPhotos.map((photo, photoIndex) => ({
      id: photo.id,
      sortOrder: photoIndex + 1,
    }));

    setSavingAction('reorder-case-photos');
    try {
      await reorderDoctorCircleCasePhotos(photoOrders);
      toast.success('Case photos reordered.');
      await loadProfile();
    } catch (error) {
      toast.error(getFriendlyError(error));
    } finally {
      setSavingAction('');
    }
  };

  const updateAvailabilityRow = (index, field, value) => {
    setAvailabilityRows((currentRows) =>
      currentRows.map((row, rowIndex) =>
        rowIndex === index ? { ...row, [field]: value } : row
      )
    );
  };

  const addAvailabilityRow = () => {
    setAvailabilityRows((currentRows) => [...currentRows, { ...EMPTY_AVAILABILITY_ROW }]);
  };

  const removeAvailabilityRow = (index) => {
    setAvailabilityRows((currentRows) =>
      currentRows.length <= 1
        ? [{ ...EMPTY_AVAILABILITY_ROW }]
        : currentRows.filter((_, rowIndex) => rowIndex !== index)
    );
  };

  const saveAvailability = async () => {
    if (!profile?.id) {
      toast.error('Save your consultant profile before adding availability.');
      return;
    }

    setSavingAction('availability');
    try {
      const savedAvailability = await updateDoctorCircleAvailability(buildAvailabilityPayload(availabilityRows));
      setProfile((currentProfile) => ({
        ...(currentProfile || {}),
        availability: savedAvailability,
      }));
      setAvailabilityRows(normalizeAvailabilityRows(savedAvailability));
      toast.success('Doctor Circle availability saved.');
    } catch (error) {
      toast.error(getFriendlyError(error));
    } finally {
      setSavingAction('');
    }
  };

  if (!canSearchConsultants && !canManageProfile) {
    return (
      <div className="app-page">
        <section className="section-card p-6">
          <div className="flex items-start gap-4">
            <div className="rounded-2xl bg-amber-50 p-3 text-amber-600">
              <ShieldAlert className="h-6 w-6" />
            </div>
            <div>
              <h1 className="page-title">Doctor's Circle</h1>
              <p className="page-subtitle">
                Your role cannot access Doctor's Circle from this page. Owner/Admin, Doctor, Staff if permitted, Accountant if permitted, and future Consultant Doctor roles can use the public network.
              </p>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="app-page space-y-6">
      <header className="section-card px-5 py-6 md:px-7 md:py-8">
        <h1 className="page-title">Doctor's Circle</h1>
        <p className="page-subtitle mt-2 max-w-3xl">
          Connect with trusted dental consultants, share expertise, and keep complex cases moving with confidence.
        </p>
      </header>

      <nav className="grid gap-3 rounded-[26px] border border-slate-200 bg-white p-2 shadow-sm md:grid-cols-2">
        {PRIMARY_TAB_ITEMS.map((tab) => {
          const Icon = tab.icon;
          const isActive = tab.id === 'findConsultants' ? activeTab === 'findConsultants' : isProfileWorkspaceActive;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id === 'profile' && isProfileWorkspaceActive ? activeTab : tab.id)}
              className={`inline-flex min-h-[56px] items-center justify-center gap-2 rounded-[20px] px-5 py-3 text-sm font-bold transition ${
                isActive
                  ? 'bg-gradient-to-r from-indigo-600 to-blue-600 text-white shadow-[0_16px_30px_rgba(79,70,229,0.24)]'
                  : 'bg-slate-50 text-slate-700 hover:bg-slate-100'
              }`}
            >
              <Icon className="h-4 w-4" />
              {tab.label}
            </button>
          );
        })}
      </nav>

      {isProfileWorkspaceActive ? (
        <section className="space-y-3 rounded-[26px] border border-slate-200 bg-white p-3 shadow-sm">
          <div className="rounded-[20px] border border-amber-100 bg-amber-50/70 px-4 py-3 text-sm leading-6 text-amber-800">
            <span className="font-semibold">Privacy note: </span>
            Avoid clinic contact details, phone numbers, patient names, or private clinic information in public profile and availability fields.
          </div>
          <nav className="flex gap-2 overflow-x-auto">
            {PROFILE_WORKSPACE_TAB_ITEMS.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`inline-flex min-w-fit items-center gap-2 rounded-[16px] px-4 py-2.5 text-sm font-semibold transition ${
                    isActive ? 'bg-slate-900 text-white shadow-[0_10px_20px_rgba(15,23,42,0.18)]' : 'text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </section>
      ) : null}

      {loading ? (
        <section className="section-card p-6">
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-600">
            <Loader2 className="h-4 w-4 animate-spin text-indigo-600" />
            Loading Doctor's Circle profile...
          </div>
        </section>
      ) : loadingError ? (
        <section className="section-card p-6">
          <div className="flex items-start gap-3 text-rose-700">
            <ShieldAlert className="mt-0.5 h-5 w-5" />
            <div>
              <p className="font-semibold">Unable to load Doctor's Circle.</p>
              <p className="mt-1 text-sm">{loadingError}</p>
              <button type="button" className="btn-secondary mt-4" onClick={loadProfile}>
                Try Again
              </button>
            </div>
          </div>
        </section>
      ) : (
        <>
          {activeTab === 'profile' ? (
            canManageProfile ? (
            <section className="section-card p-5 md:p-6">
              <div className="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,1fr)_320px]">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <Field label="Display Name *" warning={hasContactLikeText(profileForm.displayName) ? 'This may contain contact details.' : ''}>
                    <input className="input-modern" value={profileForm.displayName} onChange={(event) => updateProfileForm('displayName', event.target.value)} placeholder="Dr. Name" />
                  </Field>
                  <Field label="Degree">
                    <input className="input-modern" value={profileForm.degree} onChange={(event) => updateProfileForm('degree', event.target.value)} placeholder="MDS, BDS, Fellowship" />
                  </Field>
                  <Field label="Education">
                    <textarea className="textarea-modern min-h-[112px]" value={profileForm.education} onChange={(event) => updateProfileForm('education', event.target.value)} placeholder="Education and training summary" />
                  </Field>
                  <Field label="Specialization">
                    <textarea className="textarea-modern min-h-[112px]" value={profileForm.specialization} onChange={(event) => updateProfileForm('specialization', event.target.value)} placeholder="Orthodontist, Endodontist, Implantologist" />
                  </Field>
                  <Field label="Years of Experience">
                    <input className="input-modern" type="number" min="0" step="1" value={profileForm.yearsOfExperience} onChange={(event) => updateProfileForm('yearsOfExperience', event.target.value)} placeholder="0" />
                  </Field>
                  <Field label={`Fees Per Service (${RUPEE_SYMBOL})`}>
                    <input className="input-modern" type="number" min="0" step="0.01" value={profileForm.feesPerService} onChange={(event) => updateProfileForm('feesPerService', event.target.value)} placeholder="0.00" />
                  </Field>
                  <Field label="Area of Availability" warning={hasContactLikeText(profileForm.areaOfAvailability) ? 'This may contain contact details.' : ''}>
                    <input className="input-modern" value={profileForm.areaOfAvailability} onChange={(event) => updateProfileForm('areaOfAvailability', event.target.value)} placeholder="Andheri West, South Delhi, Indiranagar" />
                  </Field>
                  <Field label="Time of Availability">
                    <input className="input-modern" value={profileForm.timeOfAvailability} onChange={(event) => updateProfileForm('timeOfAvailability', event.target.value)} placeholder="Mon-Fri evenings, Sundays 10 AM-2 PM" />
                  </Field>
                  <div className="md:col-span-2">
                    <Field label="Short Public Message" warning={hasContactLikeText(profileForm.shortMessage) ? 'This may contain contact details.' : ''}>
                      <textarea className="textarea-modern min-h-[130px]" value={profileForm.shortMessage} onChange={(event) => updateProfileForm('shortMessage', event.target.value)} placeholder="Short introduction for clinics. Do not add phone, email, clinic address, patient names, or private clinic details." />
                    </Field>
                  </div>
                  <div className="md:col-span-2">
                    <Field label="Profile Photo">
                      <div className="rounded-[22px] border border-slate-200 bg-slate-50 p-4">
                        <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
                          <ProfileAvatar
                            src={profilePhotoPreviewUrl || profileForm.profilePhotoUrl}
                            name={profileForm.displayName || profile?.displayName || 'Doctor'}
                            className="h-28 w-28 shrink-0 rounded-full"
                            initialsClassName="text-2xl"
                          />
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-semibold text-slate-900">
                              {selectedProfilePhoto
                                ? selectedProfilePhoto.name
                                : profileForm.profilePhotoUrl
                                  ? 'Current profile photo'
                                  : 'Add a clear profile photo'}
                            </p>
                            <p className="mt-1 text-xs leading-5 text-slate-500">
                              JPEG, PNG, or WebP. Maximum 5 MB. On supported phones, Choose Photo opens the camera or gallery.
                            </p>
                            {profilePhotoUploading ? (
                              <div className="mt-3" aria-live="polite">
                                <div className="h-2 overflow-hidden rounded-full bg-slate-200">
                                  <div className="h-full rounded-full bg-blue-600 transition-all" style={{ width: `${profilePhotoProgress || 8}%` }} />
                                </div>
                                <p className="mt-1 text-xs font-semibold text-blue-700">
                                  Uploading{profilePhotoProgress ? ` ${profilePhotoProgress}%` : '...'}
                                </p>
                              </div>
                            ) : null}
                          </div>
                        </div>

                        <input
                          ref={profilePhotoInputRef}
                          type="file"
                          accept="image/jpeg,image/png,image/webp"
                          className="sr-only"
                          onChange={selectProfilePhoto}
                          disabled={profilePhotoUploading || profilePhotoRemoving}
                          aria-label="Choose Doctor Circle profile photo"
                        />

                        {profilePhotoError ? (
                          <p className="mt-3 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm font-semibold text-rose-700" role="alert">
                            {profilePhotoError}
                          </p>
                        ) : null}

                        <div className="mt-4 flex flex-wrap gap-2">
                          <button
                            type="button"
                            className="btn-secondary !rounded-xl !px-4 !py-2.5 !text-xs"
                            onClick={() => profilePhotoInputRef.current?.click()}
                            disabled={profilePhotoUploading || profilePhotoRemoving}
                          >
                            <Camera className="h-4 w-4" />
                            {profileForm.profilePhotoUrl || selectedProfilePhoto ? 'Replace Photo' : 'Choose Photo'}
                          </button>
                          {selectedProfilePhoto ? (
                            <>
                              <button
                                type="button"
                                className="btn-primary !rounded-xl !px-4 !py-2.5 !text-xs"
                                onClick={uploadProfilePhoto}
                                disabled={profilePhotoUploading || profilePhotoRemoving}
                              >
                                {profilePhotoUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                                {profilePhotoUploading ? 'Uploading...' : 'Upload Photo'}
                              </button>
                              <button
                                type="button"
                                className="btn-secondary !rounded-xl !px-4 !py-2.5 !text-xs"
                                onClick={clearSelectedProfilePhoto}
                                disabled={profilePhotoUploading}
                              >
                                Cancel
                              </button>
                            </>
                          ) : null}
                          {profile?.profilePhotoUrl ? (
                            <button
                              type="button"
                              className="inline-flex min-h-10 items-center gap-2 rounded-xl border border-rose-200 bg-white px-4 py-2.5 text-xs font-semibold text-rose-600 transition hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-60"
                              onClick={removeProfilePhoto}
                              disabled={profilePhotoUploading || profilePhotoRemoving}
                            >
                              {profilePhotoRemoving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                              {profilePhotoRemoving ? 'Removing...' : 'Remove Photo'}
                            </button>
                          ) : null}
                        </div>
                      </div>
                    </Field>
                  </div>
                  <label className="md:col-span-2 flex items-center gap-3 rounded-[18px] border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-700">
                    <input type="checkbox" checked={profileForm.isOpenToWork} onChange={(event) => updateProfileForm('isOpenToWork', event.target.checked)} className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500" />
                    Open to work
                  </label>
                </div>

                <aside className="space-y-4">
                  <div className="rounded-[22px] border border-slate-200 bg-slate-50 p-4">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">Safety Check</p>
                    {warnings.length ? (
                      <div className="mt-3 space-y-2">
                        {warnings.map((warning) => (
                          <p key={warning} className="rounded-2xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-700">
                            {warning}
                          </p>
                        ))}
                      </div>
                    ) : (
                      <p className="mt-3 rounded-2xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-700">
                        No obvious contact details detected.
                      </p>
                    )}
                  </div>

                  <div className="rounded-[22px] border border-slate-200 bg-white p-4">
                    <p className="text-sm font-semibold text-slate-900">Profile Actions</p>
                    <div className="mt-4 grid gap-2">
                      <button type="button" className="btn-secondary !justify-start" onClick={() => saveProfile()} disabled={Boolean(savingAction)}>
                        {savingAction === 'save' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                        Save Draft
                      </button>
                      <button type="button" className="btn-primary !justify-start" onClick={publishProfile} disabled={Boolean(savingAction)}>
                        {savingAction === 'publish' ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                        Publish Profile
                      </button>
                      <button type="button" className="btn-secondary !justify-start" onClick={pauseProfile} disabled={Boolean(savingAction) || !profile?.id}>
                        <PauseCircle className="h-4 w-4" />
                        Pause Profile
                      </button>
                      <button type="button" className="inline-flex items-center justify-start gap-2 rounded-2xl border border-rose-200 bg-white px-5 py-3 text-sm font-semibold text-rose-600 transition hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-60" onClick={archiveProfile} disabled={Boolean(savingAction) || !profile?.id}>
                        <Archive className="h-4 w-4" />
                        Archive Profile
                      </button>
                    </div>
                  </div>
                </aside>
              </div>
            </section>
            ) : (
              <RestrictedManagementPanel />
            )
          ) : null}

          {activeTab === 'casePhotos' ? (
            canManageProfile ? (
            <section className="grid grid-cols-1 gap-5 xl:grid-cols-[minmax(0,1fr)_380px]">
              <div className="section-card p-5 md:p-6">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h2 className="text-lg font-semibold text-slate-900">My Case Photos</h2>
                    <p className="mt-1 text-sm text-slate-500">Only approved photos can appear publicly. Maximum 5 active photos.</p>
                  </div>
                  <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-600">
                    {activeCasePhotoCount} / 5
                  </span>
                </div>

                <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2">
                  {casePhotos.length ? (
                    casePhotos.map((photo, index) => (
                      <div key={photo.id} className="rounded-[22px] border border-slate-200 bg-white p-4">
                        <div className="aspect-[4/3] overflow-hidden rounded-[18px] border border-slate-200 bg-slate-50">
                          {photo.imageUrl ? (
                            <img src={photo.imageUrl} alt="Doctor Circle case" className="h-full w-full object-cover" />
                          ) : (
                            <div className="flex h-full items-center justify-center text-slate-400">
                              <ImagePlus className="h-8 w-8" />
                            </div>
                          )}
                        </div>
                        <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
                          <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-semibold text-slate-600">
                            {statusLabel(photo.photoType)}
                          </span>
                          <span className={`rounded-full border px-2.5 py-1 text-xs font-semibold ${statusClasses(photo.moderationStatus)}`}>
                            {statusLabel(photo.moderationStatus)}
                          </span>
                        </div>
                        <div className="mt-3 flex flex-wrap gap-2">
                          <button type="button" className="btn-secondary !rounded-xl !px-3 !py-2 !text-xs" onClick={() => moveCasePhoto(index, -1)} disabled={index === 0 || Boolean(savingAction)}>
                            <ArrowUp className="h-3.5 w-3.5" />
                            Up
                          </button>
                          <button type="button" className="btn-secondary !rounded-xl !px-3 !py-2 !text-xs" onClick={() => moveCasePhoto(index, 1)} disabled={index === casePhotos.length - 1 || Boolean(savingAction)}>
                            <ArrowDown className="h-3.5 w-3.5" />
                            Down
                          </button>
                          <button type="button" className="inline-flex items-center gap-2 rounded-xl border border-rose-200 bg-white px-3 py-2 text-xs font-semibold text-rose-600 transition hover:bg-rose-50 disabled:opacity-60" onClick={() => archiveCasePhoto(photo)} disabled={Boolean(savingAction)}>
                            <X className="h-3.5 w-3.5" />
                            Archive
                          </button>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="md:col-span-2 rounded-[22px] border border-dashed border-slate-300 bg-slate-50 px-5 py-10 text-center">
                      <Camera className="mx-auto h-8 w-8 text-slate-400" />
                      <p className="mt-3 text-sm font-semibold text-slate-900">No case photos added yet.</p>
                      <p className="mt-1 text-sm text-slate-500">Add public portfolio images only after removing all identifiers.</p>
                    </div>
                  )}
                </div>
              </div>

              <aside className="section-card p-5">
                <h3 className="text-base font-semibold text-slate-900">Choose Case Photo</h3>
                <p className="mt-1 text-sm text-slate-500">JPEG, PNG, or WebP, up to 5 MB. Every new photo is submitted for moderation.</p>
                <div className="mt-4 space-y-4">
                  <input ref={casePhotoInputRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={selectCasePhoto} className="sr-only" disabled={!canAddCasePhoto || casePhotoUploading} />
                  {casePhotoPreviewUrl ? (
                    <div className="space-y-3">
                      <div className="aspect-[4/3] max-h-72 overflow-hidden rounded-[18px] border border-slate-200 bg-slate-50">
                        <img src={casePhotoPreviewUrl} alt="Selected case photo preview" className="h-full w-full object-cover" />
                      </div>
                      <p className="break-all text-xs text-slate-500">{selectedCasePhoto?.name}</p>
                      <div className="flex flex-wrap gap-2">
                        <button type="button" className="btn-secondary !px-3 !py-2 !text-xs" onClick={() => casePhotoInputRef.current?.click()} disabled={casePhotoUploading}>
                          <ImagePlus className="h-3.5 w-3.5" /> Replace selected photo
                        </button>
                        <button type="button" className="btn-secondary !px-3 !py-2 !text-xs" onClick={clearSelectedCasePhoto} disabled={casePhotoUploading}>
                          <X className="h-3.5 w-3.5" /> Cancel selection
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button type="button" className="btn-secondary w-full" onClick={() => casePhotoInputRef.current?.click()} disabled={!canAddCasePhoto || casePhotoUploading}>
                      <ImagePlus className="h-4 w-4" /> Choose Case Photo
                    </button>
                  )}
                  <Field label="Photo Type">
                    <select className="input-modern" value={casePhotoForm.photoType} onChange={(event) => setCasePhotoForm((current) => ({ ...current, photoType: event.target.value }))} disabled={!canAddCasePhoto || casePhotoUploading}>
                      <option value="portfolio">Portfolio</option>
                      <option value="before">Before</option>
                      <option value="after">After</option>
                      <option value="combined">Combined Before/After</option>
                    </select>
                  </Field>
                  <label className="flex items-start gap-3 rounded-[18px] border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-700">
                    <input type="checkbox" checked={casePhotoForm.containsNoTextOrNumbersAttested} onChange={(event) => setCasePhotoForm((current) => ({ ...current, containsNoTextOrNumbersAttested: event.target.checked }))} className="mt-1 h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500" disabled={!canAddCasePhoto || casePhotoUploading} />
                    I confirm this image contains no patient identity, no text, and no numbers.
                  </label>
                  {!canAddCasePhoto ? <p className="rounded-xl bg-amber-50 px-3 py-2 text-sm font-medium text-amber-800">Maximum 5 active case photos reached. Remove one before uploading another.</p> : null}
                  {casePhotoError ? <p role="alert" className="rounded-xl bg-rose-50 px-3 py-2 text-sm font-medium text-rose-700">{casePhotoError}</p> : null}
                  {casePhotoUploading ? <p className="text-sm font-semibold text-indigo-700">Uploading{casePhotoProgress ? ` ${casePhotoProgress}%` : '...'}</p> : null}
                  <button type="button" className="btn-primary w-full" onClick={addCasePhoto} disabled={!canAddCasePhoto || !selectedCasePhoto || !casePhotoForm.containsNoTextOrNumbersAttested || casePhotoUploading}>
                    {casePhotoUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                    Upload &amp; Submit for Moderation
                  </button>
                </div>
              </aside>
            </section>
            ) : (
              <RestrictedManagementPanel />
            )
          ) : null}

          {activeTab === 'availability' ? (
            canManageProfile ? (
            <section className="section-card p-5 md:p-6">
              <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">My Availability</h2>
                  <p className="mt-1 text-sm text-slate-500">Add area, days, and time windows where clinics can later find you.</p>
                </div>
                <button type="button" className="btn-secondary" onClick={addAvailabilityRow}>
                  <MapPin className="h-4 w-4" />
                  Add Row
                </button>
              </div>

              <div className="mt-5 space-y-4">
                {availabilityRows.map((row, index) => (
                  <div key={`availability-${index}`} className="rounded-[22px] border border-slate-200 bg-slate-50 p-4">
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                      <Field label="Area Name">
                        <input className="input-modern" value={row.areaName} onChange={(event) => updateAvailabilityRow(index, 'areaName', event.target.value)} placeholder="Area / locality" />
                      </Field>
                      <Field label="Available Days">
                        <input className="input-modern" value={row.availableDaysText} onChange={(event) => updateAvailabilityRow(index, 'availableDaysText', event.target.value)} placeholder="Mon, Wed, Fri" />
                      </Field>
                      <Field label="Start Time">
                        <input className="input-modern" type="time" value={row.startTime} onChange={(event) => updateAvailabilityRow(index, 'startTime', event.target.value)} />
                      </Field>
                      <Field label="End Time">
                        <input className="input-modern" type="time" value={row.endTime} onChange={(event) => updateAvailabilityRow(index, 'endTime', event.target.value)} />
                      </Field>
                    </div>
                    <div className="mt-4">
                      <Field label="Notes">
                        <textarea className="textarea-modern min-h-[90px]" value={row.notes} onChange={(event) => updateAvailabilityRow(index, 'notes', event.target.value)} placeholder="Optional availability notes. Do not enter phone, email, patient names, or private clinic details." />
                      </Field>
                    </div>
                    <div className="mt-4 flex items-center justify-between gap-3">
                      <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                        <input type="checkbox" checked={row.isActive} onChange={(event) => updateAvailabilityRow(index, 'isActive', event.target.checked)} className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500" />
                        Active
                      </label>
                      <button type="button" className="inline-flex items-center gap-2 rounded-xl border border-rose-200 bg-white px-3 py-2 text-xs font-semibold text-rose-600 transition hover:bg-rose-50" onClick={() => removeAvailabilityRow(index)}>
                        <X className="h-3.5 w-3.5" />
                        Remove Row
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-5 flex justify-end">
                <button type="button" className="btn-primary" onClick={saveAvailability} disabled={Boolean(savingAction)}>
                  {savingAction === 'availability' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  Save Availability
                </button>
              </div>
            </section>
            ) : (
              <RestrictedManagementPanel />
            )
          ) : null}

          {activeTab === 'sentOffers' ? (
            canCreateConsultOffers ? (
              <section className="space-y-5">
                <div className="section-card p-5 md:p-6">
                  <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                    <div>
                      <div className="inline-flex items-center gap-2 rounded-full border border-blue-100 bg-blue-50 px-3 py-1.5 text-xs font-semibold text-blue-700">
                        <ClipboardList className="h-3.5 w-3.5" />
                        My Sent Offers
                      </div>
                      <h2 className="mt-3 text-xl font-bold tracking-tight text-slate-900">Clinic consult requests sent from this tenant</h2>
                      <p className="mt-1 max-w-3xl text-sm leading-6 text-slate-500">
                        This list is scoped by the backend to your clinic. It does not show other clinics' offers and does not mix with the internal Consultant Fees Ledger.
                      </p>
                    </div>
                    <button type="button" className="btn-secondary" onClick={() => loadSentOffers({ offset: 0 })} disabled={sentOffersLoading}>
                      {sentOffersLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                      Refresh
                    </button>
                  </div>
                </div>

                {sentOffersError ? (
                  <section className="rounded-[22px] border border-rose-200 bg-rose-50 px-5 py-4 text-rose-700">
                    <div className="flex items-start gap-3">
                      <ShieldAlert className="mt-0.5 h-5 w-5" />
                      <div>
                        <p className="text-sm font-semibold">Unable to load sent offers.</p>
                        <p className="mt-1 text-sm">{sentOffersError}</p>
                      </div>
                    </div>
                  </section>
                ) : null}

                {sentOffersLoading ? (
                  <section className="section-card p-6">
                    <div className="flex items-center gap-2 text-sm font-semibold text-slate-600">
                      <Loader2 className="h-4 w-4 animate-spin text-indigo-600" />
                      Loading sent offers...
                    </div>
                  </section>
                ) : sentOffers.length ? (
                  <section className="space-y-4">
                    {sentOffers.map((offer) => (
                      <OfferCard
                        key={offer.id}
                        offer={offer}
                        type="sent"
                        onView={openOfferDetails}
                        onCancel={cancelOffer}
                        onMarkViewed={markIncomingOfferViewed}
                        onAccept={acceptIncomingOffer}
                        onDecline={openDeclineOfferModal}
                        onUnlock={initiateUnlockPayment}
                        onOpenChat={openChatForOffer}
                        onReport={openReportModal}
                        actionLoading={offerActionLoading}
                      />
                    ))}
                    <div className="flex flex-wrap items-center justify-between gap-3 rounded-[22px] border border-slate-200 bg-white px-4 py-3">
                      <p className="text-sm font-semibold text-slate-500">
                        Showing {sentOffers.length} of {sentOffersPagination.total || sentOffers.length}
                      </p>
                      <div className="flex gap-2">
                        <button type="button" className="btn-secondary !rounded-xl !px-3 !py-2 !text-xs" disabled={sentOffersLoading || Number(sentOffersPagination.offset || 0) <= 0} onClick={() => loadSentOffers({ offset: Math.max(0, Number(sentOffersPagination.offset || 0) - Number(sentOffersPagination.limit || 20)) })}>
                          Previous
                        </button>
                        <button type="button" className="btn-secondary !rounded-xl !px-3 !py-2 !text-xs" disabled={sentOffersLoading || Number(sentOffersPagination.offset || 0) + Number(sentOffersPagination.limit || 20) >= Number(sentOffersPagination.total || 0)} onClick={() => loadSentOffers({ offset: Number(sentOffersPagination.offset || 0) + Number(sentOffersPagination.limit || 20) })}>
                          Next
                        </button>
                      </div>
                    </div>
                  </section>
                ) : (
                  <section className="section-card p-8 text-center">
                    <ClipboardList className="mx-auto h-9 w-9 text-slate-400" />
                    <h3 className="mt-4 text-lg font-semibold text-slate-900">No sent offers yet.</h3>
                    <p className="mx-auto mt-2 max-w-xl text-sm leading-6 text-slate-500">
                      Use Find Consultants, open a public consultant card, and send a safe consult request.
                    </p>
                  </section>
                )}
              </section>
            ) : (
              <section className="section-card p-6">
                <div className="flex items-start gap-4">
                  <div className="rounded-2xl bg-amber-50 p-3 text-amber-600">
                    <ShieldAlert className="h-6 w-6" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-slate-900">Sending consult offers is restricted</h2>
                    <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-500">
                      Owner/Admin and Doctor roles can send clinical consult requests. Staff and Accountant roles cannot send Doctor's Circle consult offers.
                    </p>
                  </div>
                </div>
              </section>
            )
          ) : null}

          {activeTab === 'incomingOffers' ? (
            canViewIncomingOffers ? (
              <section className="space-y-5">
                <div className="section-card p-5 md:p-6">
                  <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                    <div>
                      <div className="inline-flex items-center gap-2 rounded-full border border-indigo-100 bg-indigo-50 px-3 py-1.5 text-xs font-semibold text-indigo-700">
                        <Inbox className="h-3.5 w-3.5" />
                        Incoming Offers
                      </div>
                      <h2 className="mt-3 text-xl font-bold tracking-tight text-slate-900">Privacy-safe incoming consult requests</h2>
                      <p className="mt-1 max-w-3xl text-sm leading-6 text-slate-500">
                        Incoming offers are tied to your Doctor's Circle profile. Clinic identity, clinic contact, patient identity, patient contact, tenant IDs, appointment IDs, and internal ledger data stay hidden before unlock/payment.
                      </p>
                    </div>
                    <button type="button" className="btn-secondary" onClick={() => loadIncomingOffers({ offset: 0 })} disabled={incomingOffersLoading}>
                      {incomingOffersLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                      Refresh
                    </button>
                  </div>
                </div>

                {incomingOffersError ? (
                  <section className="rounded-[22px] border border-rose-200 bg-rose-50 px-5 py-4 text-rose-700">
                    <div className="flex items-start gap-3">
                      <ShieldAlert className="mt-0.5 h-5 w-5" />
                      <div>
                        <p className="text-sm font-semibold">Unable to load incoming offers.</p>
                        <p className="mt-1 text-sm">{incomingOffersError}</p>
                      </div>
                    </div>
                  </section>
                ) : null}

                {incomingOffersLoading ? (
                  <section className="section-card p-6">
                    <div className="flex items-center gap-2 text-sm font-semibold text-slate-600">
                      <Loader2 className="h-4 w-4 animate-spin text-indigo-600" />
                      Loading incoming offers...
                    </div>
                  </section>
                ) : incomingOffers.length ? (
                  <section className="space-y-4">
                    {incomingOffers.map((offer) => (
                      <OfferCard
                        key={offer.id}
                        offer={offer}
                        type="incoming"
                        onView={openOfferDetails}
                        onCancel={cancelOffer}
                        onMarkViewed={markIncomingOfferViewed}
                        onAccept={acceptIncomingOffer}
                        onDecline={openDeclineOfferModal}
                        onUnlock={initiateUnlockPayment}
                        onOpenChat={openChatForOffer}
                        onReport={openReportModal}
                        actionLoading={offerActionLoading}
                      />
                    ))}
                    <div className="flex flex-wrap items-center justify-between gap-3 rounded-[22px] border border-slate-200 bg-white px-4 py-3">
                      <p className="text-sm font-semibold text-slate-500">
                        Showing {incomingOffers.length} of {incomingOffersPagination.total || incomingOffers.length}
                      </p>
                      <div className="flex gap-2">
                        <button type="button" className="btn-secondary !rounded-xl !px-3 !py-2 !text-xs" disabled={incomingOffersLoading || Number(incomingOffersPagination.offset || 0) <= 0} onClick={() => loadIncomingOffers({ offset: Math.max(0, Number(incomingOffersPagination.offset || 0) - Number(incomingOffersPagination.limit || 20)) })}>
                          Previous
                        </button>
                        <button type="button" className="btn-secondary !rounded-xl !px-3 !py-2 !text-xs" disabled={incomingOffersLoading || Number(incomingOffersPagination.offset || 0) + Number(incomingOffersPagination.limit || 20) >= Number(incomingOffersPagination.total || 0)} onClick={() => loadIncomingOffers({ offset: Number(incomingOffersPagination.offset || 0) + Number(incomingOffersPagination.limit || 20) })}>
                          Next
                        </button>
                      </div>
                    </div>
                  </section>
                ) : (
                  <section className="section-card p-8 text-center">
                    <Inbox className="mx-auto h-9 w-9 text-slate-400" />
                    <h3 className="mt-4 text-lg font-semibold text-slate-900">No incoming offers yet.</h3>
                    <p className="mx-auto mt-2 max-w-xl text-sm leading-6 text-slate-500">
                      New Doctor's Circle consult requests will appear here after clinics send offers to your public consultant profile.
                    </p>
                  </section>
                )}

                <section className="rounded-[22px] border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                  Accept/Decline and unlock payment foundation are enabled. Private chat opens only after successful paid/unlocked status.
                </section>
              </section>
            ) : (
              <section className="section-card p-6">
                <div className="flex items-start gap-4">
                  <div className="rounded-2xl bg-amber-50 p-3 text-amber-600">
                    <ShieldAlert className="h-6 w-6" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-slate-900">Incoming offers are restricted</h2>
                    <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-500">
                      Owner/Admin, Doctor, and future Consultant Doctor roles can view incoming offers tied to their public Doctor's Circle profile. Staff and Accountant roles are restricted.
                    </p>
                  </div>
                </div>
              </section>
            )
          ) : null}

          {activeTab === 'findConsultants' ? (
            <section className="space-y-5">
              <form onSubmit={submitSearch} className="section-card overflow-hidden">
                <div className="border-b border-slate-100 bg-gradient-to-br from-white via-indigo-50/40 to-blue-50/60 p-5 md:p-6">
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                  <div>
                    <div className="inline-flex items-center gap-2 rounded-full border border-blue-100 bg-blue-50 px-3 py-1.5 text-xs font-semibold text-blue-700">
                      <Search className="h-3.5 w-3.5" />
                      Find Consultants
                    </div>
                    <h2 className="mt-3 text-xl font-bold tracking-tight text-slate-900">The right specialist is one search away</h2>
                    <p className="mt-1 max-w-3xl text-sm leading-6 text-slate-500">
                      Search by specialty, qualification, location, and availability. Only public profile details appear here — never private clinic, patient, or financial information.
                    </p>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <button type="submit" className="btn-primary" disabled={consultantsLoading}>
                      {consultantsLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                      Search
                    </button>
                    <button type="button" className="btn-secondary" onClick={clearSearch} disabled={consultantsLoading}>
                      <RefreshCw className="h-4 w-4" />
                      Reset
                    </button>
                  </div>
                </div>

                  <div className="mt-5 rounded-[26px] border border-indigo-100 bg-white p-4 shadow-sm">
                    <label className="block text-xs font-bold uppercase tracking-[0.18em] text-indigo-600" htmlFor="doctor-circle-specialization-search">
                      Specialization
                    </label>
                    <div className="mt-3 flex items-center gap-3 rounded-[22px] border border-slate-200 bg-slate-50 px-4 py-3 focus-within:border-indigo-300 focus-within:bg-white focus-within:ring-4 focus-within:ring-indigo-50">
                      <Search className="h-5 w-5 shrink-0 text-indigo-500" />
                      <input
                        id="doctor-circle-specialization-search"
                        className="min-h-[44px] w-full bg-transparent text-base font-semibold text-slate-900 outline-none placeholder:text-slate-400 md:text-lg"
                        value={searchFilters.specialization}
                        onChange={(event) => updateSearchFilter('specialization', event.target.value)}
                        placeholder="Search by specialization - e.g. Orthodontist, Endodontist"
                        list="doctor-circle-specialization-options"
                      />
                    </div>
                    <datalist id="doctor-circle-specialization-options">
                      {SPECIALIZATION_SEARCH_OPTIONS.map((option) => (
                        <option key={option} value={option} />
                      ))}
                    </datalist>
                  </div>
                </div>

                <div className="p-5 md:p-6">
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                    <Field label="Education">
                      <select className="input-modern" value={searchFilters.education} onChange={(event) => updateSearchFilter('education', event.target.value)}>
                        <option value="">Any</option>
                        <option value="BDS">BDS</option>
                        <option value="MDS">MDS</option>
                        <option value="Fellowship">Fellowship</option>
                      </select>
                    </Field>
                    <Field label="Minimum Years of Experience">
                      <input
                        className="input-modern"
                        type="number"
                        min="0"
                        max="100"
                        step="1"
                        value={searchFilters.minimumYearsExperience}
                        onChange={(event) => updateSearchFilter('minimumYearsExperience', event.target.value)}
                        placeholder="0"
                      />
                    </Field>
                    <Field label="Area of Availability">
                      <input
                        className="input-modern"
                        value={searchFilters.area}
                        onChange={(event) => updateSearchFilter('area', event.target.value)}
                        placeholder="City, locality, area"
                        list="doctor-circle-area-options"
                      />
                    </Field>
                    <Field label="Time of Availability">
                      <input
                        className="input-modern"
                        value={searchFilters.availability}
                        onChange={(event) => updateSearchFilter('availability', event.target.value)}
                        placeholder="Evenings, weekends, after 2 PM"
                        list="doctor-circle-availability-options"
                      />
                    </Field>
                  </div>

                  <datalist id="doctor-circle-area-options">
                    {AREA_SEARCH_OPTIONS.map((option) => (
                      <option key={option} value={option} />
                    ))}
                  </datalist>
                  <datalist id="doctor-circle-availability-options">
                    {AVAILABILITY_SEARCH_OPTIONS.map((option) => (
                      <option key={option} value={option} />
                    ))}
                  </datalist>

                  <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end">
                    <Field label="Optional keyword">
                      <input className="input-modern" value={searchFilters.search} onChange={(event) => updateSearchFilter('search', event.target.value)} placeholder="Name, degree, clinic-safe profile keyword" />
                    </Field>
                    <div className="flex flex-wrap items-center gap-3">
                      <label className="inline-flex min-h-[44px] items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-4 py-2 text-sm font-semibold text-slate-700">
                        <input type="checkbox" checked={searchFilters.openToWork} onChange={(event) => updateSearchFilter('openToWork', event.target.checked)} className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500" />
                        Open to work only
                      </label>
                      <label className="inline-flex min-h-[44px] items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-4 py-2 text-sm font-semibold text-slate-700">
                        <Filter className="h-4 w-4 text-slate-500" />
                        Sort
                        <select value={searchFilters.sortBy} onChange={(event) => updateSearchFilter('sortBy', event.target.value)} className="bg-transparent text-sm font-semibold text-slate-700 outline-none">
                          <option value="relevance">Relevance</option>
                          <option value="updated_desc">Recently Updated</option>
                        </select>
                      </label>
                    </div>
                  </div>

                  <p className="mt-4 text-sm font-semibold text-slate-500">{searchResultsSummary}</p>
                </div>
              </form>

              {consultantsError ? (
                <section className="rounded-[22px] border border-rose-200 bg-rose-50 px-5 py-4 text-rose-700">
                  <div className="flex items-start gap-3">
                    <ShieldAlert className="mt-0.5 h-5 w-5" />
                    <div>
                      <p className="text-sm font-semibold">Unable to load public consultant profiles.</p>
                      <p className="mt-1 text-sm">{consultantsError}</p>
                    </div>
                  </div>
                </section>
              ) : null}

              {consultantsLoading ? (
                <section className="section-card p-6">
                  <div className="flex items-center gap-2 text-sm font-semibold text-slate-600">
                    <Loader2 className="h-4 w-4 animate-spin text-indigo-600" />
                    Searching public consultant profiles...
                  </div>
                </section>
              ) : consultants.length ? (
                <section className="space-y-4">
                  {consultants.map((consultant) => (
                    <ConsultantCard
                      key={consultant.id}
                      consultant={consultant}
                      onOpenDetails={openConsultantDetails}
                      onConsult={openConsultOfferModal}
                      onReport={openReportModal}
                      canCreateOffers={canCreateConsultOffers}
                    />
                  ))}

                  <div className="flex flex-wrap items-center justify-between gap-3 rounded-[22px] border border-slate-200 bg-white px-4 py-3">
                    <p className="text-sm font-semibold text-slate-500">
                      Showing {consultants.length} of {consultantPagination.total || consultants.length}
                    </p>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        className="btn-secondary !rounded-xl !px-3 !py-2 !text-xs"
                        disabled={consultantsLoading || Number(consultantPagination.offset || 0) <= 0}
                        onClick={() => runConsultantSearch({ offset: Math.max(0, Number(consultantPagination.offset || 0) - Number(consultantPagination.limit || 20)) })}
                      >
                        Previous
                      </button>
                      <button
                        type="button"
                        className="btn-secondary !rounded-xl !px-3 !py-2 !text-xs"
                        disabled={
                          consultantsLoading ||
                          Number(consultantPagination.offset || 0) + Number(consultantPagination.limit || 20) >= Number(consultantPagination.total || 0)
                        }
                        onClick={() => runConsultantSearch({ offset: Number(consultantPagination.offset || 0) + Number(consultantPagination.limit || 20) })}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </section>
              ) : (
                <section className="section-card p-8 text-center">
                  <Search className="mx-auto h-9 w-9 text-slate-400" />
                  <h3 className="mt-4 text-lg font-semibold text-slate-900">No consultants found.</h3>
                  <p className="mx-auto mt-2 max-w-xl text-sm leading-6 text-slate-500">
                    Try a broader name, education, specialization, area, or availability search. Only published and approved open-to-work profiles appear here.
                  </p>
                </section>
              )}

              <section className="rounded-[22px] border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-800">
                Dektal will notify consultants safely when a consult request is sent. Private chat remains locked until successful unlock payment.
              </section>

              {selectedConsultant || detailsLoading ? (
                <ConsultantDetailsModal
                  consultant={selectedConsultant}
                  loading={detailsLoading}
                  onClose={() => {
                    setSelectedConsultant(null);
                    setDetailsLoading(false);
                  }}
                  onConsult={openConsultOfferModal}
                  onReport={openReportModal}
                  canCreateOffers={canCreateConsultOffers}
                />
              ) : null}
            </section>
          ) : null}

          {offerModalConsultant ? (
            <ConsultOfferModal
              consultant={offerModalConsultant}
              form={offerForm}
              warnings={offerWarnings}
              sending={offerSending}
              onClose={closeConsultOfferModal}
              onChange={updateOfferForm}
              onSubmit={submitConsultOffer}
            />
          ) : null}

          {selectedOffer ? (
            <OfferDetailModal
              offer={selectedOffer}
              type={selectedOfferType}
              onClose={() => {
                setSelectedOffer(null);
                setSelectedOfferType('');
              }}
              onCancel={cancelOffer}
              onMarkViewed={markIncomingOfferViewed}
              onAccept={acceptIncomingOffer}
              onDecline={openDeclineOfferModal}
              onUnlock={initiateUnlockPayment}
              onOpenChat={openChatForOffer}
              onReport={openReportModal}
              actionLoading={offerActionLoading}
            />
          ) : null}

          {chatThread ? (
            <ChatModal
              thread={chatThread}
              messages={chatMessages}
              value={chatText}
              loading={chatLoading}
              sending={chatSending}
              error={chatError}
              onChange={setChatText}
              onClose={closeChatModal}
              onRefresh={refreshChatMessages}
              onReport={openReportModal}
              onSubmit={submitChatMessage}
            />
          ) : null}

          {reportContext ? (
            <ReportModal
              context={reportContext}
              form={reportForm}
              submitting={reportSubmitting}
              onChange={updateReportForm}
              onClose={closeReportModal}
              onSubmit={submitReport}
            />
          ) : null}

          {declineModalOffer ? (
            <DeclineOfferModal
              offer={declineModalOffer}
              form={declineForm}
              onChange={updateDeclineForm}
              onClose={closeDeclineOfferModal}
              onSubmit={submitDeclineOffer}
              submitting={offerActionLoading === `decline-${declineModalOffer.id}`}
            />
          ) : null}
        </>
      )}
    </div>
  );
}
